// src/Dashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import logo from "./logo.png";
import WarrantyTab from "./WarrantyTab";
import WarrantyList from "./WarrantyList";
import IncidentList from "./IncidentList";



/**
 * Dashboard.js
 *
 * ✅ MODS (this update):
 * - Persist and display Customer Link (token + URL) after switching pages / refreshing
 *   by hydrating from GET /incidents/{id} response (inc.customerLink).
 * - Show link status: Active / Expired / Disabled
 * - Do NOT force regenerate if expired (still display, but disable "Open")
 * - Disable "Open" when expired or disabled
 *
 * ✅ FIX (Investigation not rehydrating):
 * - Your investigations_get returns { item: inv }, but UI expected inv directly.
 *   We now unwrap invResp.item everywhere.
 * - Determine Reiri/other per-investigation (schema/productType) instead of per-incident only.
 * - Add isReiriInv state and use it for:
 *    - selectInvestigation normalization
 *    - patchInvestigation normalization
 *    - choosing Form vs JSON editor
 *
 * ✅ FIX (Broken customer URL like "https:///customer?..."):
 * - Never trust persisted customerLink.url unless it's a valid absolute http(s) URL.
 * - Always rebuild URL from env REACT_APP_PUBLIC_CUSTOMER_URL + token when stored URL is invalid.
 * - buildCustomerUrl() validates base URL (must have hostname), otherwise returns "".
 */

const STATUSES = ["DRAFT", "OPEN_TO_CUSTOMER", "CUSTOMER_SUBMITTED", "CLOSED", "ARCHIVED"];

const STATUS_LABELS = {
  DRAFT: "Draft",
  OPEN_TO_CUSTOMER: "Open",
  CUSTOMER_SUBMITTED: "Submitted",
  CLOSED: "Closed",
  ARCHIVED: "Archived",
  DELETED: "Deleted",
};

// Dropdown options (Admin/Agent Create Incident)
const PRODUCT_OPTIONS = ["Reiri", "MARUTTO-P", "MARUTTO-E"];

const AFFILIATE_OPTIONS = [
  "DABR",
  "DENV",
  "DMMO",
  "DALA",
  "DASI",
  "DAME",
  "DAC",
  "DAV",
  "DMSS",
  "DAMX",
  "DID",
  "HOTAI",
  "DAIPL",
  "DPH",
  "DHK",
  "SDS",
  "DNA",
  "DAS",
  "DNZ",
  "DARG",
  "DSP",
];

/* ===========================
   Helpers
=========================== */

async function apiFetch(path, { method = "GET", body, headers } = {}) {
  const base = process.env.REACT_APP_API_BASE;
  if (!base) throw new Error("Missing REACT_APP_API_BASE in .env");

  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();

  // ✅ Always use ID token for API Gateway Cognito authorizer (REST API setup)
  if (!idToken) {
    throw new Error("Missing ID token (please sign out/in again)");
  }

  const res = await fetch(`${base}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    throw new Error(msg.includes("HTTP") ? msg : `${msg} (HTTP ${res.status})`);
  }
  return data;
}

function prettyTime(epochSeconds) {
  if (!epochSeconds) return "-";
  try {
    return new Date(epochSeconds * 1000).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(epochSeconds);
  }
}

function safeJsonParse(text, fallback = null) {
  try {
    return JSON.parse(text);
  } catch {
    return fallback;
  }
}

function isValidAbsoluteHttpUrl(u) {
  if (!u) return false;

  const s = String(u).trim();

  // ❌ Explicitly reject empty-host URLs like "https:///customer?..."
  if (/^https?:\/\/\//i.test(s)) return false;

  try {
    const url = new URL(s);
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;

    // Must have a hostname (prevents empty-host cases)
    if (!url.hostname) return false;

    return true;
  } catch {
    return false;
  }
}


/**
 * Build customer URL using explicit env base.
 * - Requires REACT_APP_PUBLIC_CUSTOMER_URL to be an absolute URL with hostname.
 * - Returns "" if base is missing/invalid (prevents "https:///customer?...").
 */
function buildCustomerUrl({ incidentId, token }) {
  if (!incidentId || !token) return "";

  const rawBase = (process.env.REACT_APP_PUBLIC_CUSTOMER_URL || "").trim();
  const base = rawBase.replace(/\/+$/, "");

  if (!base) return "";

  // validate base is absolute and has hostname
  try {
    const u = new URL(base);
    if (!u.hostname) return "";
    if (u.protocol !== "http:" && u.protocol !== "https:") return "";
  } catch {
    return "";
  }

  return `${base}/customer?incidentId=${encodeURIComponent(incidentId)}&token=${encodeURIComponent(token)}`;
}

function buildServiceRequestUrl({ incidentId, token }) {
  if (!incidentId || !token) return "";

  const rawBase =
    (process.env.REACT_APP_PUBLIC_SERVICE_REQUEST_URL ||
      process.env.REACT_APP_PUBLIC_CUSTOMER_URL ||
      "").trim();

  const base = rawBase.replace(/\/+$/, "");
  if (!base) return "";

  try {
    const u = new URL(base);
    if (!u.hostname) return "";
    if (u.protocol !== "http:" && u.protocol !== "https:") return "";
  } catch {
    return "";
  }

  return `${base}/service-request?incidentId=${encodeURIComponent(
    incidentId
  )}&token=${encodeURIComponent(token)}`;
}

function renderReadableObject(obj, indent = 0) {
  if (!obj || typeof obj !== "object") return null;

  return Object.entries(obj).map(([key, value]) => {
    const label = key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (c) => c.toUpperCase());

    if (value && typeof value === "object" && !Array.isArray(value)) {
      return (
        <div key={key} style={{ marginLeft: indent * 16 }}>
          <div style={{ fontWeight: 600, marginTop: 12 }}>{label}</div>
          {renderReadableObject(value, indent + 1)}
        </div>
      );
    }

    return (
      <div
        key={key}
        style={{
          display: "flex",
          gap: 12,
          marginLeft: indent * 16,
          marginTop: 6,
        }}
      >
        <div style={{ width: 180, color: "#64748b" }}>{label}</div>
        <div style={{ fontWeight: 500, color: "#1e293b" }}>
          {String(value || "-")}
        </div>
      </div>
    );
  });
}



function formatBytes(bytes) {
  if (bytes == null) return "-";
  const units = ["B", "KB", "MB", "GB"];
  let b = Number(bytes);
  let i = 0;
  while (b >= 1024 && i < units.length - 1) {
    b /= 1024;
    i++;
  }
  return `${b.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * ✅ Hydrate customer link from incident payload (persisted by backend).
 * Supports:
 *   inc.customerLink = { token, expiresAt, validDays, isDisabled, generatedAt, url? }
 *
 * ✅ IMPORTANT:
 * - We DO NOT trust cl.url unless it is a valid absolute http(s) URL.
 * - If invalid/missing, we rebuild from env base + token.
 */
function hydrateCustomerLinkFromIncident(inc, incidentId) {
  if (!inc) return null;

  const cl = inc.customerLink;
  if (!cl || typeof cl !== "object") return null;

  const token = (cl.token || "").toString();
  const expiresAt = cl.expiresAt != null ? Number(cl.expiresAt) : null;
  const validDays = cl.validDays != null ? Number(cl.validDays) : null;
  const isDisabled = !!cl.isDisabled;
  const generatedAt = cl.generatedAt != null ? Number(cl.generatedAt) : null;

  const rebuilt = token ? buildCustomerUrl({ incidentId, token }) : "";
  const url = isValidAbsoluteHttpUrl(cl.url) ? cl.url : rebuilt;

  return {
    incidentId,
    token,
    expiresAt,
    validDays,
    isDisabled,
    generatedAt,
    url,
  };
}

function hydrateServiceRequestLinkFromIncident(inc, incidentId) {
  const sr =
    inc?.serviceRequestLink && typeof inc.serviceRequestLink === "object"
      ? inc.serviceRequestLink
      : null;

  if (!sr) return null;

  const id = inc?.incidentId || incidentId;
  const token = sr.token || null;

  const url =
    token && typeof buildServiceRequestUrl === "function"
      ? buildServiceRequestUrl({ incidentId: id, token })
      : null;

  return {
    incidentId: id,
    token,                 // ✅ now preserved when available
    url,                   // ✅ rebuilt automatically
    expiresAt: sr.expiresAt,
    validDays: sr.validDays,
    isDisabled: !!sr.isDisabled,
    generatedAt: sr.generatedAt,
    generatedBy: sr.generatedBy,
  };
}


async function handleLogout() {
  const ok = window.confirm("Log out from this application?");
  if (!ok) return;

  try {
    await signOut();
    window.location.reload();
  } catch (err) {
    console.error("Logout failed", err);
  }
}

/* ===========================
   Customer HTML + Inline Images
=========================== */

// Extract s3Key from URL or return raw if it's already a key.
function extractS3KeyFromUrl(src) {
  if (!src) return "";
  const s = String(src).trim();
  if (!s) return "";

  try {
    const u = new URL(s, window.location.origin);
    const path = u.pathname || "";
    const key = decodeURIComponent(path.replace(/^\/+/, ""));
    return key;
  } catch {
    return s;
  }
}

function sanitizeCustomerHtml(html) {
  if (!html) return "";

  const tpl = document.createElement("template");
  tpl.innerHTML = String(html);

  const allowedTags = new Set([
    "P",
    "BR",
    "DIV",
    "SPAN",
    "B",
    "STRONG",
    "I",
    "EM",
    "U",
    "S",
    "UL",
    "OL",
    "LI",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
    "BLOCKQUOTE",
    "PRE",
    "CODE",
    "A",
    "IMG",
  ]);

  const allowedAttrs = new Set(["href", "src", "alt", "title", "target", "rel", "style"]);

  const walk = (node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName;

      if (!allowedTags.has(tag)) {
        const parent = node.parentNode;
        if (parent) parent.removeChild(node);
        return;
      }

      const attrs = [...node.attributes].map((a) => a.name);
      for (const name of attrs) {
        const n = name.toLowerCase();
        const v = (node.getAttribute(name) || "").trim();

        if (n.startsWith("on")) {
          node.removeAttribute(name);
          continue;
        }

        if ((n === "href" || n === "src") && /^javascript:/i.test(v)) {
          node.removeAttribute(name);
          continue;
        }

        if (!allowedAttrs.has(n)) {
          node.removeAttribute(name);
          continue;
        }
      }

      if (tag === "A") {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
      }

      if (tag === "IMG") {
        node.style.maxWidth = "100%";
        node.style.height = "auto";
        node.style.borderRadius = "8px";
      }
    }

    const children = [...node.childNodes];
    for (const c of children) walk(c);
  };

  walk(tpl.content);
  return tpl.innerHTML;
}

async function presignImagesInHtml({ incidentId, html }) {
  if (!incidentId || !html) return html;

  const tpl = document.createElement("template");
  tpl.innerHTML = String(html);

  const imgs = [...tpl.content.querySelectorAll("img")];
  if (imgs.length === 0) return tpl.innerHTML;

  const candidates = [];
  for (const img of imgs) {
    const src = img.getAttribute("src") || "";
    const key = extractS3KeyFromUrl(src);

    const ok =
      key.startsWith(`incidents/${incidentId}/editor-images/`) ||
      key.startsWith(`public/incident/${incidentId}/editor-images/`);

    if (ok) candidates.push({ img, key });
  }
  if (candidates.length === 0) return tpl.innerHTML;

  const uniqueKeys = [...new Set(candidates.map((c) => c.key))];
  const keyToUrl = new Map();

  await Promise.all(
    uniqueKeys.map(async (k) => {
      try {
        const data = await apiFetch(`/incidents/${encodeURIComponent(incidentId)}/editor-images/download-presign`, {
          method: "POST",
          body: { s3Key: k },
        });
        const url = data.downloadUrl || data.url || data.presignedUrl;
        if (url) keyToUrl.set(k, url);
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.warn("presign failed:", k, e?.message || e);
        }
      }
    })
  );

  for (const { img, key } of candidates) {
    const url = keyToUrl.get(key);
    if (url) img.setAttribute("src", url);
  }

  return tpl.innerHTML;
}

/* ===========================
   Search / Sort / My-only
=========================== */

function normalizeStr(v) {
  return (v ?? "").toString().trim().toLowerCase();
}

function normalizeZendeskTicketNumber(v) {
  const s = String(v || "").trim();
  // keep only digits so "ZD-600", "#600", " 600 " all become "600"
  return s.replace(/[^\d]/g, "");
}

function buildZendeskTicketUrl(ticketNo) {
  const n = normalizeZendeskTicketNumber(ticketNo);
  if (!n) return "";
  return `https://daikinsolutions.zendesk.com/agent/tickets/${n}`;
}

function matchesSearch(inc, q) {
  const query = normalizeStr(q);
  if (!query) return true;

  const id = normalizeStr(inc?.incidentId);

  // ✅ match report number too (top-level or incidentData)
  const reportNo = normalizeStr(inc?.reportNo || inc?.incidentData?.reportNo || "");

  const affiliate = normalizeStr(inc?.incidentData?.affiliate || inc?.incidentData?.customerName || inc?.customerName);
  const site = normalizeStr(inc?.incidentData?.site || inc?.site);
  const subject = normalizeStr(inc?.incidentData?.subject || inc?.incidentData?.issue || "");
  const product = normalizeStr(inc?.incidentData?.product || "");

  return id.includes(query) || reportNo.includes(query) || affiliate.includes(query) || site.includes(query) || subject.includes(query) || product.includes(query);
}

// basic jwt payload decode (no deps)
function decodeJwtPayload(jwt) {
  try {
    const part = jwt.split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(b64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getIncidentOwnerSub(inc) {
  return (
    inc?.ownerSub ||
    inc?.owner ||
    inc?.createdBySub ||
    inc?.createdBy ||
    inc?.userSub ||
    inc?.incidentData?.ownerSub ||
    inc?.incidentData?.owner ||
    inc?.incidentData?.createdBySub ||
    inc?.incidentData?.createdBy ||
    null
  );
}

/* ===========================
   Readable Incident Rendering
=========================== */

const READABLE_FIELDS = [
  { key: "reportNo", label: "Incident Report No" },
  { key: "affiliate", label: "Affiliate" },
  { key: "product", label: "Product" },
  { key: "zendeskTicketNumber", label: "Zendesk Ticket Number" },
  { key: "subject", label: "Subject", multiline: true },
];

function isPlainObject(v) {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function valueToDisplay(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map((x) => (typeof x === "string" ? x : JSON.stringify(x))).join(", ");
  if (isPlainObject(v)) return JSON.stringify(v, null, 2);
  return String(v);
}

function getReadableValue(obj, key) {
  if (!obj) return "";

  if (key === "reportNo") return obj.reportNo ?? "";
  if (key === "affiliate") return obj.affiliate ?? obj.customerName ?? "";
  if (key === "subject") return obj.subject ?? obj.issue ?? "";
  if (key === "product") return obj.product ?? "";
  if (key === "site") return obj.site ?? "";
  return obj[key] ?? "";
}

/* ===========================
   Customer Response Rendering
=========================== */

const CUSTOMER_RESPONSE_FIELDS = [
  { key: "reportedBy", label: "Reported By" },
  { key: "affiliateEmail", label: "Affiliate Email" },
  { key: "customer", label: "Customer" },
  { key: "siteLocation", label: "Site Location" },
  { key: "commissioningDate", label: "Commissioning Date", type: "date" },
  { key: "incidentDate", label: "Incident Date", type: "date" },
  { key: "controllerModel", label: "Controller Model" },
  { key: "productSerialNumber", label: "Product Serial Number" },
  { key: "softwareVersion", label: "Software Version" },
  { key: "description", label: "Description", multiline: true },

  { key: "name", label: "Name (legacy)", hiddenIfSameAs: "reportedBy" },
  { key: "message", label: "Message (legacy)", hiddenIfSameAs: "description", multiline: true },
];

function formatMaybeDate(value) {
  if (!value) return "";
  const s = String(value).trim();
  const d = new Date(s);
  if (!Number.isNaN(d.getTime()) && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
    try {
      return d.toLocaleDateString([], { dateStyle: "medium" });
    } catch {
      return s;
    }
  }
  return s;
}

function getCustomerResponseValue(obj, field) {
  if (!obj) return "";
  const v = obj[field.key];

  if (field.hiddenIfSameAs) {
    const other = obj[field.hiddenIfSameAs];
    if (String(v || "").trim() && String(other || "").trim() && String(v).trim() === String(other).trim()) {
      return "";
    }
  }

  if (field.type === "date") return formatMaybeDate(v);
  return valueToDisplay(v);
}

function getCustomerRawHtml(customerResponseObj) {
  if (!customerResponseObj) return "";
  return customerResponseObj.descriptionHtml || customerResponseObj.description || customerResponseObj.message || "";
}

function getReiriPrefillFromAffiliateResponse(customerResponseObj) {
  if (!customerResponseObj || typeof customerResponseObj !== "object") return null;

  const customer = (customerResponseObj.customer || "").toString().trim();

  const reportedBy = (customerResponseObj.reportedBy || customerResponseObj.name || "").toString().trim();

  // Affiliate form uses "Product Serial Number"
  // Map it into Investigation "Controller Serial Number"
  const controllerSerialNumber = (customerResponseObj.productSerialNumber || customerResponseObj.controllerSerialNumber || "").toString().trim();

  const softwareVersion = (customerResponseObj.softwareVersion || "").toString().trim();

  if (!customer && !reportedBy && !controllerSerialNumber && !softwareVersion) return null;

  return { customer, reportedBy, controllerSerialNumber, softwareVersion };
}

/* ===========================
   Investigations: Reiri Template + Form Helpers
=========================== */

const YES_NO_OPTIONS = ["Yes", "No"];
const FUNC_TEST_OPTIONS = ["OK", "NG", "Not tested", "N/A"];

function deepGet(obj, path, fallback = "") {
  try {
    const parts = String(path).split(".").filter(Boolean);
    let cur = obj;
    for (const p of parts) {
      if (!cur || typeof cur !== "object") return fallback;
      cur = cur[p];
    }
    return cur == null ? fallback : cur;
  } catch {
    return fallback;
  }
}

function deepSet(obj, path, value) {
  const parts = String(path).split(".").filter(Boolean);
  if (!parts.length) return obj;
  const clone = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  let cur = clone;
  for (let i = 0; i < parts.length; i++) {
    const key = parts[i];
    const last = i === parts.length - 1;

    if (last) {
      cur[key] = value;
    } else {
      const next = cur[key];
      const nextObj = next && typeof next === "object" && !Array.isArray(next) ? { ...next } : {};
      cur[key] = nextObj;
      cur = nextObj;
    }
  }
  return clone;
}

function mergeDefaults(base, defaults) {
  const out = { ...(defaults || {}) };
  const src = base && typeof base === "object" ? base : {};
  for (const k of Object.keys(src)) {
    if (
      src[k] &&
      typeof src[k] === "object" &&
      !Array.isArray(src[k]) &&
      out[k] &&
      typeof out[k] === "object" &&
      !Array.isArray(out[k])
    ) {
      out[k] = mergeDefaults(src[k], out[k]);
    } else {
      out[k] = src[k];
    }
  }
  return out;
}

function todayISO() {
  try {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
}

function buildReiriInvestigationTemplate({ affiliate = "", customer = "", incidentId = "", incidentReportNo = "" } = {}) {
  return {
    schema: "reiri-investigation-v1",
    productType: "Reiri",
    report: {
      countryAffiliate: affiliate || "",
      customer: customer || "",
      incidentReportNo: incidentReportNo || "",
      date: todayISO(),
      reportBy: "",
      controllerSerialNumber: "",
      softwareVersion: "",
      signatureAttachmentId: "",
      preparedBy: "",
      preparedDate: todayISO(),
    },
    hardwareReview: {
      q1_packaging: "",
      q2_noDamage: "",
      q3_noModification: "",
    },
    functionalityTest: {
      powerConnection: "",
      networkConnection: "",
      rs485Connection: "",
      usbPortConnection: "",
      dioConnection: "",
      touchPanelResponse: "",
    },
    remarks: "",
    incidentDescription: "",
    evaluationAndAnalysis: "",
    actionTaken: "",
    notes: "",
    meta: {
      clientCreatedAt: new Date().toISOString(),
      incidentId: incidentId || "",
    },
  };
}

function getSingleInvestigationId({ incidentId, incidentReportNo }) {
  const base = (incidentReportNo || incidentId || "").toString().trim();
  if (!base) return "";
  return `${base}-INV`;
}

function validateReiriRequiredSelections(invObj) {
  const missing = [];

  const checks = [
    { path: "hardwareReview.q1_packaging", label: "Hardware Review: Packaging / Labelling" },
    { path: "hardwareReview.q2_noDamage", label: "Hardware Review: No mechanical damage" },
    { path: "hardwareReview.q3_noModification", label: "Hardware Review: No modification" },

    { path: "functionalityTest.powerConnection", label: "Functionality Test: Power Connection" },
    { path: "functionalityTest.networkConnection", label: "Functionality Test: Network Connection" },
    { path: "functionalityTest.rs485Connection", label: "Functionality Test: RS485 Connection" },
    { path: "functionalityTest.usbPortConnection", label: "Functionality Test: USB Port Connection" },
    { path: "functionalityTest.dioConnection", label: "Functionality Test: DIO Connection" },
    { path: "functionalityTest.touchPanelResponse", label: "Functionality Test: Touch Panel Response" },
  ];

  for (const c of checks) {
    const v = String(deepGet(invObj, c.path, "") || "").trim();
    if (!v) missing.push(c.label);
  }

  return missing;
}

function normalizeReiriInvestigation(obj, ctx) {
  const defaults = buildReiriInvestigationTemplate(ctx);
  const merged = mergeDefaults(obj, defaults);

  if (!merged.schema) merged.schema = defaults.schema;
  if (!merged.productType) merged.productType = "Reiri";

  return merged;
}

/* ===========================
   UI Components
=========================== */

function DetailRow({ label, value, multiline }) {
  if (!value) return null;
  const isLong = multiline || (typeof value === "string" && value.length > 90);
  return (
    <div style={{ padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
      <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div
        style={{
          fontSize: 13,
          color: "#1e293b",
          fontWeight: 500,
          whiteSpace: isLong ? "pre-wrap" : "normal",
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Segmented({ value, onChange, options, disabled }) {
  return (
    <div style={{ display: "inline-flex", background: "#f1f5f9", padding: 4, borderRadius: 10 }}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            style={{
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 600,
              cursor: disabled ? "not-allowed" : "pointer",
              border: "none",
              borderRadius: 8,
              background: active ? "white" : "transparent",
              color: active ? "#1e293b" : "#64748b",
              boxShadow: active ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function statusColor(status) {
  switch ((status || "").toUpperCase()) {
    case "DRAFT":
      return { bg: "#f1f5f9", fg: "#475569", bd: "#e2e8f0" };
    case "OPEN_TO_CUSTOMER":
      return { bg: "#ecfeff", fg: "#0891b2", bd: "#a5f3fc" };
    case "CUSTOMER_SUBMITTED":
      return { bg: "#eef2ff", fg: "#4f46e5", bd: "#c7d2fe" };
    case "CLOSED":
      return { bg: "#f0fdf4", fg: "#16a34a", bd: "#bbf7d0" };
    case "ARCHIVED":
      return { bg: "#faf5ff", fg: "#7c3aed", bd: "#e9d5ff" };
    case "DELETED":
      return { bg: "#fef2f2", fg: "#991b1b", bd: "#fecaca" };
    default:
      return { bg: "#f1f5f9", fg: "#475569", bd: "#e2e8f0" };
  }
}

function Pill({ children, status }) {
  const c = statusColor(status);
  return (
    <span
      style={{
        display: "inline-flex",
        padding: "2px 10px",
        borderRadius: 999,
        background: c.bg,
        border: `1px solid ${c.bd}`,
        color: c.fg,
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function IconButton({ children, onClick, disabled, title }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      title={title}
      style={{
        border: "1px solid #e2e8f0",
        background: "white",
        color: "#1e293b",
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

function PrimaryButton({ children, onClick, disabled, title }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      title={title}
      style={{
        border: "none",
        background: "#1e293b",
        color: "white",
        borderRadius: 8,
        padding: "8px 16px",
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      {children}
    </button>
  );
}

function DangerButton({ children, onClick, disabled, title }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      title={title}
      style={{
        border: "1px solid #fecaca",
        background: "#fef2f2",
        color: "#991b1b",
        borderRadius: 8,
        padding: "8px 16px",
        fontSize: 13,
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, disabled, title }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      title={title}
      style={{
        border: "1px solid #e2e8f0",
        background: "white",
        color: "#1e293b",
        borderRadius: 8,
        padding: "8px 16px",
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

function Card({ title, right, children, noPadding }) {
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, background: "white", overflow: "hidden" }}>
      {(title || right) && (
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#fcfdfe",
            gap: 12,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{title}</div>
          <div>{right}</div>
        </div>
      )}
      <div style={{ padding: noPadding ? 0 : 16 }}>{children}</div>
    </div>
  );
}

function CodeArea({ value, onChange, rows = 10, disabled, readOnly }) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      rows={rows}
      disabled={disabled}
      readOnly={readOnly}
      style={{
        width: "100%",
        borderRadius: 8,
        border: "none",
        padding: 12,
        fontFamily: "ui-monospace, monospace",
        fontSize: 12,
        background: "#0f172a",
        color: "#94a3b8",
        outline: "none",
        resize: "vertical",
        opacity: disabled ? 0.6 : 1,
      }}
    />
  );
}

function Toast({ message }) {
  if (!message) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        background: "#1e293b",
        color: "white",
        padding: "10px 20px",
        borderRadius: 12,
        fontWeight: 600,
        fontSize: 13,
        boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
        zIndex: 9999,
      }}
    >
      {message}
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, disabled, required }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>
        {label} {required ? <span style={{ color: "#ef4444" }}>*</span> : null}
      </div>
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "10px",
          borderRadius: 10,
          border: "1px solid #e2e8f0",
          fontSize: 13,
          outline: "none",
        }}
      />
    </div>
  );
}

function TextAreaField({ label, value, onChange, placeholder, disabled, required, rows = 4 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>
        {label} {required ? <span style={{ color: "#ef4444" }}>*</span> : null}
      </div>
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        style={{
          width: "100%",
          padding: "10px",
          borderRadius: 10,
          border: "1px solid #e2e8f0",
          fontSize: 13,
          outline: "none",
          resize: "vertical",
        }}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, disabled, required, placeholder = "Select..." }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>
        {label} {required ? <span style={{ color: "#ef4444" }}>*</span> : null}
      </div>

      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "10px",
          borderRadius: 10,
          border: "1px solid #e2e8f0",
          fontSize: 13,
          outline: "none",
          background: "white",
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

function SelectFieldKV({ label, value, onChange, options, disabled, required, placeholder = "Select..." }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>
        {label} {required ? <span style={{ color: "#ef4444" }}>*</span> : null}
      </div>

      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "10px",
          borderRadius: 10,
          border: "1px solid #e2e8f0",
          fontSize: 13,
          outline: "none",
          background: "white",
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ===========================
   Archive Mode Helpers
=========================== */

function getIncidentProduct(inc) {
  const p = inc?.incidentData?.product;
  return (p && String(p).trim()) || "Unknown";
}

function getIncidentAffiliate(inc) {
  return inc?.incidentData?.affiliate || inc?.incidentData?.customerName || "-";
}

function getIncidentSubject(inc) {
  return inc?.incidentData?.subject || inc?.incidentData?.issue || "-";
}

/* ===========================
   Main Dashboard
=========================== */

export default function Dashboard() {
  const [statusFilter, setStatusFilter] = useState("DRAFT");
  const [incidents, setIncidents] = useState([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);

  const [selectedIncidentId, setSelectedIncidentId] = useState(null);
  const [incidentDetails, setIncidentDetails] = useState(null);

  const [investigations, setInvestigations] = useState([]);
  const [selectedInvestigationId, setSelectedInvestigationId] = useState(null);
  const [investigationDetails, setInvestigationDetails] = useState(null);
  const [invAttachments, setInvAttachments] = useState([]);

  const [globalError, setGlobalError] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  // Create incident form fields (MINIMAL)
  const [createAffiliate, setCreateAffiliate] = useState("");
  const [createProduct, setCreateProduct] = useState("");
  const [createZendeskTicketNumber, setCreateZendeskTicketNumber] = useState("");
  const [createSubject, setCreateSubject] = useState("");

  // edit incident JSON
  const [incidentEditJson, setIncidentEditJson] = useState("{}");
  const [isEditingIncident, setIsEditingIncident] = useState(false);

  // investigations edit: JSON (fallback) + structured draft (Reiri)
  const [editInvestigationJson, setEditInvestigationJson] = useState("{}");
  const [invDraftObj, setInvDraftObj] = useState(null);
  const [invEditorMode, setInvEditorMode] = useState("form"); // form | json (for Reiri); json only for others

  // ✅ per-investigation type flag
  const [isReiriInv, setIsReiriInv] = useState(false);

  // customer link
  const [genDays, setGenDays] = useState(3);
  const [customerLink, setCustomerLink] = useState(null);

  // ✅ service request link
const [srDays, setSrDays] = useState(3);
const [serviceRequestLink, setServiceRequestLink] = useState(null);

  // upload
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState("");

  // tabs
  const [activeTab, setActiveTab] = useState("Overview");

  // left-side controls
  const [searchTerm, setSearchTerm] = useState("");
  const [sortUpdated, setSortUpdated] = useState("updated_desc");
  const [myOnly, setMyOnly] = useState(false);
  const [currentUserSub, setCurrentUserSub] = useState(null);
  const [currentUserLabel, setCurrentUserLabel] = useState("");
  const [useMySignature, setUseMySignature] = useState(false);


    // Separate view mode so "Create" doesn't collide with tab state
const [mainView, setMainView] = useState("detail"); // detail | create | profile
const [returnView, setReturnView] = useState("detail"); // where to go back after profile

    // ===========================
  // Profile Signature Upload (S3 presign + confirm)
  // ===========================
  const [sigFile, setSigFile] = useState(null);
  const [sigBusy, setSigBusy] = useState(false);
  const [sigStatus, setSigStatus] = useState("");

  async function uploadProfileSignature() {
    if (!sigFile) {
      setSigStatus("Please select an image file first.");
      return;
    }

    // backend currently allows png/jpeg only (your presign lambda)
    var ct = sigFile.type || "image/png";
    var name = sigFile.name || "signature.png";

    setSigBusy(true);
    setSigStatus("Requesting upload link...");

    try {
      // 1) presign (API Gateway -> Lambda -> S3 presigned PUT)
      var pre = await apiFetch("/profile/signature/presign", {
        method: "POST",
        body: { filename: name, contentType: ct },
      });

      if (!pre || !pre.uploadUrl || !pre.key) {
        throw new Error("Presign did not return uploadUrl/key.");
      }

      if (pre.maxBytes && sigFile.size > pre.maxBytes) {
        throw new Error("File too large. Max " + pre.maxBytes + " bytes.");
      }

      // 2) upload to S3
      setSigStatus("Uploading to S3...");
      var putRes = await fetch(pre.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": pre.contentType || ct },
        body: sigFile,
      });

      if (!putRes.ok) {
        var t = "";
        try { t = await putRes.text(); } catch {}
        throw new Error("S3 upload failed: HTTP " + putRes.status + " " + t);
      }

      // 3) confirm (API Gateway -> Lambda -> DynamoDB profile update)
      setSigStatus("Confirming signature...");
      var conf = await apiFetch("/profile/signature/confirm", {
        method: "POST",
        body: {
          key: pre.key,
          filename: name,
          contentType: pre.contentType || ct,
        },
      });
      await loadProfile({ silent: true });
      await loadSignaturePreview();



      var updatedAt = (conf && conf.signature && conf.signature.updatedAt) ? Number(conf.signature.updatedAt) : 0;
      setSigStatus("Signature saved. Updated at: " + (updatedAt ? new Date(updatedAt * 1000).toLocaleString() : "-"));
      setSigFile(null);
    } catch (e) {
      setSigStatus("Upload failed: " + (e && e.message ? e.message : String(e)));
    } finally {
      setSigBusy(false);
    }
  }


  // near other profile states
const [sigPreviewUrl, setSigPreviewUrl] = useState("");
const [sigPreviewBusy, setSigPreviewBusy] = useState(false);
const [sigPreviewErr, setSigPreviewErr] = useState("");

// call this when opening Profile, or when profileSignature changes
async function loadSignaturePreview() {
  setSigPreviewErr("");
  setSigPreviewUrl("");

  if (!profileSignature?.key) return;

  setSigPreviewBusy(true);
  try {
    // Prefer s3Key (most common in your lambdas)
    let resp = await apiFetch("/profile/signature/download-presign", {
      method: "POST",
      body: { s3Key: profileSignature.key },
    });

    // Backward-compat fallback if your lambda expects "key"
    if (!resp || !(resp.url || resp.downloadUrl || resp.presignedUrl)) {
      resp = await apiFetch("/profile/signature/download-presign", {
        method: "POST",
        body: { key: profileSignature.key },
      });
    }

    const url = resp.url || resp.downloadUrl || resp.presignedUrl;
    if (!url) throw new Error("No preview URL returned");
    setSigPreviewUrl(url);
  } catch (e) {
    setSigPreviewErr(e.message || String(e));
  } finally {
    setSigPreviewBusy(false);
  }
}



  // ===========================
  // Profile (GET /profile)
  // ===========================
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // ✅ Display Name (for PREPARED BY)
const [displayName, setDisplayName] = useState("");
const [profileSaveBusy, setProfileSaveBusy] = useState(false);
const [profileSaveStatus, setProfileSaveStatus] = useState("");

  async function loadProfile({ silent } = {}) {
    setGlobalError("");
    setProfileLoading(true);
    try {
      const resp = await apiFetch("/profile", { method: "GET" });
      // backend recommended shape: { userSub, profile: itemOrNull }
      const p = resp?.profile || resp?.item || resp || null;
      // ✅ hydrate display name
      setDisplayName((p && p.displayName) ? String(p.displayName) : "");
      setProfile(p);
      if (!silent) flashToast("Profile loaded");
      return p;
    } catch (e) {
      // don't hard-fail dashboard if profile missing
      if (!silent) setGlobalError(e.message || String(e));
      return null;
    } finally {
      setProfileLoading(false);
    }
  }

  function getProfileSignature(p) {
    const sig = p?.signature;
    if (!sig || typeof sig !== "object") return null;
    const key = String(sig.key || sig.s3Key || "").trim();
    if (!key) return null;
    return {
      key,
      filename: sig.filename || "signature",
      contentType: sig.contentType || "image/png",
      updatedAt: sig.updatedAt || null,
      status: sig.status || "",
    };
  }

const profileSignature = useMemo(() => getProfileSignature(profile), [profile]);
const hasProfileSignature = !!profileSignature?.key;

async function saveDisplayName() {
  setGlobalError("");
  setProfileSaveStatus("");

  var name = (displayName || "").trim();
  if (!name) {
    setProfileSaveStatus("Display Name cannot be empty.");
    return;
  }

  setProfileSaveBusy(true);
  try {
    // ✅ Save displayName only (backend should merge/update)
    const resp = await apiFetch("/profile", {
      method: "POST", // change to "PUT" if your backend expects PUT
      body: { displayName: name },
    });

    // refresh local profile (silent)
    const p = resp?.profile || resp?.item || resp || null;
    if (p) {
      setProfile(p);
      setDisplayName((p.displayName || "").toString());
    } else {
      await loadProfile({ silent: true });
    }

    setProfileSaveStatus("Saved ✅");
    flashToast("Display Name saved");
  } catch (e) {
    setProfileSaveStatus(e.message || "Save failed");
  } finally {
    setProfileSaveBusy(false);
  }
}



// ✅ Auto-load preview when Profile page is open and signature exists
useEffect(() => {
  if (mainView !== "profile") return;
  if (!profileSignature?.key) return;

  loadSignaturePreview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [mainView, profileSignature?.key]);


  // public customer attachments viewer state
  const [custAttachments, setCustAttachments] = useState([]);
  const [custAttLoading, setCustAttLoading] = useState(false);
  const [custShowDeleted, setCustShowDeleted] = useState(false);

  // customer response view toggle
  const [customerResponseViewMode, setCustomerResponseViewMode] = useState("readable"); // readable | json

  // rendered customer HTML (with presigned inline images)
  const [customerHtml, setCustomerHtml] = useState("");
  const [customerHtmlLoading, setCustomerHtmlLoading] = useState(false);




  // Archive mode UI state
  const isArchiveMode = statusFilter === "ARCHIVED";
  const [selectedArchiveProduct, setSelectedArchiveProduct] = useState("");

  const selectedIncident = useMemo(
    () => incidents.find((x) => x.incidentId === selectedIncidentId) || null,
    [incidents, selectedIncidentId]
  );

  const selectedInvestigation = useMemo(
    () => investigations.find((x) => x.investigationId === selectedInvestigationId) || null,
    [investigations, selectedInvestigationId]
  );

  const [notificationEmail, setNotificationEmail] = useState("");


  

  function flashToast(msg) {
    setToast(msg);
    window.clearTimeout(flashToast._t);
    flashToast._t = window.setTimeout(() => setToast(""), 1500);
  }

  function parseIncidentIdParts(id) {
    // Supports: R-2026-0001 (and falls back safely)
    const s = (id ?? "").toString().trim();
    const m = s.match(/^(.*?)-(\d{4})-(\d+)$/); // prefix-year-seq
    if (!m) return { prefix: s, year: 0, seq: 0 };

    return {
      prefix: (m[1] || "").toUpperCase(),
      year: Number(m[2]) || 0,
      seq: Number(m[3]) || 0,
    };
  }

  function openProfile() {
  setReturnView(mainView === "profile" ? "detail" : mainView);
  setMainView("profile");
  }

function closeProfile() {
  setMainView(returnView || "detail");
}

function openWarrantyList() {
  setMainView("warrantyList");
}

function closeWarrantyList() {
  setMainView("detail"); // or whatever your normal dashboard view is called
}


function openIncidentList() {
  setMainView("incidentList");
}

function closeIncidentList() {
  setMainView("detail");
}

  function getArchiveSortTime(inc) {
    return Number(inc?.archivedAt || inc?.updatedAt || 0);
  }

  function sortIncidents(list, sortUpdated) {
    const arr = Array.isArray(list) ? [...list] : [];

    arr.sort((a, b) => {
      if (sortUpdated === "updated_asc" || sortUpdated === "updated_desc") {
        const av = getArchiveSortTime(a);
        const bv = getArchiveSortTime(b);
        return sortUpdated === "updated_asc" ? av - bv : bv - av;
      }

      if (sortUpdated === "id_asc" || sortUpdated === "id_desc") {
        const A = parseIncidentIdParts(a?.reportNo || a?.incidentData?.reportNo || a?.incidentId);
        const B = parseIncidentIdParts(b?.reportNo || b?.incidentData?.reportNo || b?.incidentId);

        const byYear = A.year - B.year;
        if (byYear !== 0) return sortUpdated === "id_asc" ? byYear : -byYear;

        const bySeq = A.seq - B.seq;
        if (bySeq !== 0) return sortUpdated === "id_asc" ? bySeq : -bySeq;

        const byPrefix = A.prefix.localeCompare(B.prefix);
        if (byPrefix !== 0) return byPrefix;

        const aId = (a?.reportNo || a?.incidentData?.reportNo || a?.incidentId || "").toString();
        const bId = (b?.reportNo || b?.incidentData?.reportNo || b?.incidentId || "").toString();
        return aId.localeCompare(bId);
      }

      // fallback
      const av = getArchiveSortTime(a);
      const bv = getArchiveSortTime(b);
      return bv - av;
    });

    return arr;
  }

  // Capture current user sub once
useEffect(() => {
  (async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) return;

      const payload = decodeJwtPayload(idToken);

      const sub = payload?.sub || null;
      const label =
        payload?.email ||
        payload?.["cognito:username"] ||
        payload?.name ||
        sub ||
        "";

      setCurrentUserSub(sub);
      setCurrentUserLabel(label);
      loadProfile({ silent: true });
      setEditInvestigationJson("{}");
      setIsEditingIncident(false);
    } catch {
      // ignore
    }
  })();
}, []);


  // Reset archive selection when leaving ARCHIVED + clear selection when switching status
  useEffect(() => {
    if (statusFilter !== "ARCHIVED") {
      setSelectedArchiveProduct("");
    }

    setSelectedIncidentId(null);
    setIncidentDetails(null);
    setInvestigations([]);
    setSelectedInvestigationId(null);
    setInvestigationDetails(null);
    setInvAttachments([]);
    setCustomerLink(null);
    setCustAttachments([]);
    setCustShowDeleted(false);
    setActiveTab("Overview");
    setCustomerResponseViewMode("readable");

    setCustomerHtml("");
    setCustomerHtmlLoading(false);

    setEditInvestigationJson("{}");
    setInvDraftObj(null);
    setInvEditorMode("form");
    setIsReiriInv(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  async function loadIncidents() {
    setGlobalError("");
    setLoadingIncidents(true);
    try {
      const data = await apiFetch(`/incidents?status=${encodeURIComponent(statusFilter)}&limit=50`);
      const items = data.items || [];
      setIncidents(items);

      if (selectedIncidentId && !items.some((i) => i.incidentId === selectedIncidentId)) {
        setSelectedIncidentId(null);
        setIncidentDetails(null);
        setInvestigations([]);
        setSelectedInvestigationId(null);
        setInvestigationDetails(null);
        setInvAttachments([]);
        setCustomerLink(null);
        setCustAttachments([]);
        setCustShowDeleted(false);
        setMainView("detail");

        setEditInvestigationJson("{}");
        setInvDraftObj(null);
        setInvEditorMode("form");
        setIsReiriInv(false);
      }
    } catch (e) {
      setGlobalError(e.message || String(e));
    } finally {
      setLoadingIncidents(false);
    }
  }

// --- NEW: URL Deep Linking Logic ---
  // This code runs once when the dashboard opens to check if a 
  // specific warranty was clicked from the list.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const idFromUrl = params.get("incidentId");
    const hash = window.location.hash;

    if (idFromUrl) {
      // 1. Set the view to "detail" (in case it defaults elsewhere)
      setMainView("detail");
      
      // 2. Load the specific incident from the URL
      // We pass preserveActiveTab: true so we can set the tab ourselves below
      selectIncident(idFromUrl, { preserveActiveTab: true });

      // 3. If the link has #warranty, switch to the Warranty tab
      if (hash === "#warranty") {
        setActiveTab("Warranty");
      } else {
        setActiveTab("Overview");
      }
      window.history.replaceState({}, document.title, "/");
    }
  }, []); // [] means this only runs once on startup


  useEffect(() => {
    loadIncidents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

async function selectIncident(incidentId, opts = {}) {
  const preserveServiceRequestLink = !!opts.preserveServiceRequestLink;
  const preserveActiveTab = !!opts.preserveActiveTab;

  setGlobalError("");
  setSelectedIncidentId(incidentId);
  //set signature box to false
  setUseMySignature(false);
  setSelectedInvestigationId(null);
  setInvestigationDetails(null);
  setInvAttachments([]);
  setUploadProgress("");
  setUploadFile(null);

  setCustomerLink(null);

  // ✅ Reset locally while loading (optional, but safe)
  setNotificationEmail("");

  // ✅ don't force Overview if caller wants to keep current tab
  if (!preserveActiveTab) {
    setActiveTab("Overview");
  }

  setCustAttachments([]);
  setCustShowDeleted(false);

  setCustomerResponseViewMode("readable");
  setCustomerHtml("");
  setCustomerHtmlLoading(false);

  setMainView("detail");

  setEditInvestigationJson("{}");
  setInvDraftObj(null);
  setInvEditorMode("form");
  setIsReiriInv(false);

  // ✅ IMPORTANT: do NOT clear SR link if preserving
  if (!preserveServiceRequestLink) {
    setServiceRequestLink(null);
  }

  try {
    const resp = await apiFetch(`/incidents/${encodeURIComponent(incidentId)}`);

    // ✅ unwrap { item: ... } safely
    const incItem = resp?.item || resp || null;
    setIncidentDetails(incItem);
    setIsEditingIncident(false);
    setIncidentEditJson(JSON.stringify(incItem?.incidentData || {}, null, 2));

    // ✅ hydrate notification email from the actual item
    setNotificationEmail((incItem?.notificationEmail || "").trim());

    // ✅ customer link hydration must use the actual item
    const hydratedCustomer = hydrateCustomerLinkFromIncident(incItem, incidentId);
    setCustomerLink(hydratedCustomer);

    // ✅ Service Request hydration
    try {
      const serverSr =
        typeof hydrateServiceRequestLinkFromIncident === "function"
          ? hydrateServiceRequestLinkFromIncident(incItem, incidentId)
          : null;

      if (!preserveServiceRequestLink) {
        setServiceRequestLink(serverSr);
      } else {
        // preserve local token/url if already generated client-side; merge server fields
        setServiceRequestLink((prev) => {
          if (!prev) return serverSr;

          const hasLocalToken = !!prev.token;
          if (!hasLocalToken) return serverSr;

          return {
            ...prev,
            expiresAt: serverSr?.expiresAt ?? prev.expiresAt,
            validDays: serverSr?.validDays ?? prev.validDays,
            isDisabled: serverSr?.isDisabled ?? prev.isDisabled,
            generatedAt: serverSr?.generatedAt ?? prev.generatedAt,
            generatedBy: serverSr?.generatedBy ?? prev.generatedBy,
            // keep prev.url and prev.token
          };
        });
      }
    } catch (e) {
      // don't fail incident load if SR hydration fails
    }

    // ✅ now load ONLY the single investigation
    const invId = getSingleInvestigationId({
      incidentId,
      incidentReportNo: incItem?.reportNo || incItem?.incidentData?.reportNo || "",
    });

    if (invId) {
      try {
        const invResp = await apiFetch(
          `/incidents/${encodeURIComponent(incidentId)}/investigations/${encodeURIComponent(invId)}`
        );
        const invItem = invResp?.item || invResp || null;

        setInvestigations(invItem ? [invItem] : []);

        if (invItem?.investigationId) {
          await selectInvestigation(invItem.investigationId);
        } else {
          setSelectedInvestigationId(null);
          setInvestigationDetails(null);
        }
      } catch (e) {
        setInvestigations([]);
        setSelectedInvestigationId(null);
        setInvestigationDetails(null);
      }
    } else {
      setInvestigations([]);
    }
  } catch (e) {
    setGlobalError(e.message || String(e));
  }
}





async function refreshSelectedIncident(opts = {}) {
  if (!selectedIncidentId) return;
  await selectIncident(selectedIncidentId, opts);
}


  /* -------------------------
     Incident actions
  ------------------------- */

  async function createIncident() {
    setGlobalError("");

    const affiliate = createAffiliate.trim();
    const product = createProduct.trim();
    const subject = createSubject.trim();
    const zendeskTicketNumber = createZendeskTicketNumber.trim();

    if (!affiliate || !product || !subject) {
      setGlobalError("Please fill in Affiliate, Product, and Subject.");
      return;
    }

    const incidentData = {
      affiliate,
      product,
      subject,
      ...(zendeskTicketNumber ? { zendeskTicketNumber } : {}),
      // site intentionally omitted — filled later by customer form submission
    };

    setBusy(true);
    try {
      const created = await apiFetch(`/incidents`, { method: "POST", body: { incidentData } });
      await loadIncidents();
      flashToast("Incident created");

      const newId = created?.incidentId;
      if (newId) {
        await selectIncident(newId);
      } else {
        setMainView("detail");
      }

      setCreateAffiliate("");
      setCreateProduct("");
      setCreateZendeskTicketNumber("");
      setCreateSubject("");
    } catch (e) {
      setGlobalError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

async function patchIncident({ status, incidentDataObj, notificationEmail }) {
  if (!selectedIncidentId) return;

  const finalIncidentData = incidentDataObj || incidentDetails?.incidentData || selectedIncident?.incidentData || {};
  const finalNotificationEmail = (notificationEmail ?? "").trim();

  // Optional validation (recommended)
  if (finalNotificationEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(finalNotificationEmail)) {
    flashToast("Invalid Notification Email");
    return;
  }

  setBusy(true);
  setGlobalError("");
  try {
    await apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}`, {
      method: "PATCH",
      body: {
        incidentData: finalIncidentData,
        notificationEmail: finalNotificationEmail, // ✅ NEW
        ...(status ? { status } : {}),
      },
    });
    await refreshSelectedIncident();
    await loadIncidents();
    flashToast("Saved");
  } catch (e) {
    setGlobalError(e.message || String(e));
  } finally {
    setBusy(false);
  }
}


  async function closeCase() {
    if (!selectedIncidentId) return;
    setBusy(true);
    setGlobalError("");
    try {
      await apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}/close`, { method: "POST", body: {} });
      await refreshSelectedIncident();
      await loadIncidents();
      flashToast("Closed");
    } catch (e) {
      setGlobalError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function archiveCase() {
    if (!selectedIncidentId) return;
    setBusy(true);
    setGlobalError("");
    try {
      await apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}/archive`, { method: "POST", body: {} });
      await refreshSelectedIncident();
      await loadIncidents();
      flashToast("Archived");
    } catch (e) {
      const msg = e?.message || String(e);
      if (msg.includes("409")) {
        flashToast("Already archived");
        await refreshSelectedIncident();
        await loadIncidents();
        return;
      }
      setGlobalError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function deleteIncidentSoft() {
    if (!selectedIncidentId) return;

    const affiliate = getReadableValue(incidentDetails?.incidentData || selectedIncident?.incidentData || {}, "affiliate");
    const ok = window.confirm(
      `Soft delete this incident?\n\n${selectedIncidentId}${affiliate ? ` (${affiliate})` : ""}\n\nThis will hide it from all lists.`
    );
    if (!ok) return;

    setBusy(true);
    setGlobalError("");
    try {
      await apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}/delete`, {
        method: "POST",
        body: { reason: "Deleted from dashboard" },
      });

      flashToast("Deleted");

      setSelectedIncidentId(null);
      setIncidentDetails(null);
      setInvestigations([]);
      setSelectedInvestigationId(null);
      setInvestigationDetails(null);
      setInvAttachments([]);
      setCustomerLink(null);
      setCustAttachments([]);
      setCustShowDeleted(false);

      setEditInvestigationJson("{}");
      setInvDraftObj(null);
      setInvEditorMode("form");
      setIsReiriInv(false);

      await loadIncidents();
      setMainView("detail");
    } catch (e) {
      setGlobalError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function downloadArchivePdf() {
    if (!selectedIncidentId) return;
    setBusy(true);
    setGlobalError("");
    try {
      const data = await apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}/pdf-download-presign`, {
        method: "POST",
        body: {},
      });
      const url = data.downloadUrl || data.url || data.presignedUrl;
      if (!url) throw new Error("No download URL returned from server");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setGlobalError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  /* -------------------------
     Customer link actions
  ------------------------- */

  async function generateOrReopenCustomerLink() {
    if (!selectedIncidentId) return;
    setBusy(true);
    setGlobalError("");
    try {
      const days = Math.max(1, Math.min(30, Number(genDays || 3)));
      const data = await apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}/customer-link/generate`, {
        method: "POST",
        body: { days },
      });

      const linkObj = {
        incidentId: data.incidentId || selectedIncidentId,
        token: data.token,
        expiresAt: data.expiresAt,
        validDays: data.validDays ?? days,
        isDisabled: false,
      };

      // always build fresh URL from env base
      const url = buildCustomerUrl({ incidentId: linkObj.incidentId, token: linkObj.token });
      setCustomerLink({ ...linkObj, url });
      flashToast("Link generated");

      await refreshSelectedIncident();
      await loadIncidents();
    } catch (e) {
      setGlobalError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

async function generateOrReopenServiceRequestLink() {
  if (!selectedIncidentId) return;

  // ✅ preserve current tab so it doesn't jump back to Overview
  const currentTab = activeTab;

  setBusy(true);
  setGlobalError("");

  try {
    const days = Math.max(1, Math.min(30, Number(srDays || 3)));

    const data = await apiFetch(
      `/incidents/${encodeURIComponent(selectedIncidentId)}/service-request/generate`,
      { method: "POST", body: { days } }
    );

    const incidentId = data.incidentId || selectedIncidentId;
    const token = data.token;

    const url = buildServiceRequestUrl({ incidentId, token });

    // ✅ keep plaintext token + url in state (only available right now)
    setServiceRequestLink({
      incidentId,
      token,
      url,
      expiresAt: data.expiresAt,
      validDays: data.validDays ?? days,
      isDisabled: false,
    });

    flashToast("Service Request link generated");

    // ✅ refresh incident details, but:
    // - do NOT overwrite SR link (keep token/url)
    // - do NOT force tab to Overview
    await selectIncident(selectedIncidentId, {
      preserveServiceRequestLink: true,
      preserveActiveTab: true,
    });

    // ✅ ensure we remain on the tab the user was on
    setActiveTab(currentTab);

    await loadIncidents();
  } catch (e) {
    setGlobalError(e.message || String(e));
  } finally {
    setBusy(false);
  }
}




  async function disableCustomerLink() {
    if (!selectedIncidentId) return;
    setBusy(true);
    setGlobalError("");
    try {
      await apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}/customer-link/disable`, { method: "POST", body: {} });

      await refreshSelectedIncident();
      await loadIncidents();
      flashToast("Link disabled");
    } catch (e) {
      setGlobalError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  /* -------------------------
     Public customer attachments
  ------------------------- */

  async function loadCustomerAttachments(opts = {}) {
    const silent = !!opts.silent;

    if (!selectedIncidentId) return;
    setCustAttLoading(true);
    setGlobalError("");

    try {
      const data = await apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}/public-attachments`, { method: "GET" });

      const items = data.items || data.attachments || data.customerAttachments || [];
      setCustAttachments(Array.isArray(items) ? items : []);

      if (!silent) flashToast("Customer attachments loaded");
    } catch (e) {
      setGlobalError(e.message || String(e));
    } finally {
      setCustAttLoading(false);
    }
  }

  // ✅ Auto-load customer attachments when entering Affiliate tab (silent)
  useEffect(() => {
    if (!selectedIncidentId) return;
    if (activeTab !== "Affiliate") return;

    // Avoid duplicate calls
    if (custAttLoading) return;

    // Only auto-load if not loaded yet
    if (custAttachments && custAttachments.length > 0) return;

    loadCustomerAttachments({ silent: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedIncidentId]);

  async function downloadCustomerAttachment(att) {
    if (!selectedIncidentId || !att?.attachmentId) return;
    setBusy(true);
    setGlobalError("");

    try {
      const data = await apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}/public-attachments/download-presign`, {
        method: "POST",
        body: { attachmentId: att.attachmentId },
      });

      const url = data.downloadUrl || data.url || data.presignedUrl;
      if (!url) throw new Error("No download URL returned");
      window.open(url, "_blank", "noopener,noreferrer");
      flashToast("Download opened");
    } catch (e) {
      setGlobalError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  /* -------------------------
     Derived: incidentData, reportNo, etc.
  ------------------------- */

  const incidentDataObj = useMemo(() => {
    const serverObj = incidentDetails?.incidentData;
    if (isPlainObject(serverObj)) return serverObj;
    const parsed = safeJsonParse(incidentEditJson, {});
    return isPlainObject(parsed) ? parsed : {};
  }, [incidentDetails, incidentEditJson]);

  const incidentReportNo =
    incidentDetails?.reportNo ||
    incidentDetails?.incidentData?.reportNo ||
    selectedIncident?.reportNo ||
    selectedIncident?.incidentData?.reportNo ||
    "";

  const incidentDisplayId = incidentReportNo || selectedIncidentId;

  const incidentProduct = String(getReadableValue(incidentDataObj, "product") || "").trim();
  const isReiriIncident = incidentProduct === "Reiri";

  const customerResponseObj = incidentDetails?.customerResponse || null;
  const serviceRequestSubmittedAt = incidentDetails?.serviceRequestSubmittedAt || null;
  const serviceRequestDataObj = incidentDetails?.serviceRequestData || null;




  /* -------------------------
     Investigations
  ------------------------- */

  async function refreshInvestigations() {
    if (!selectedIncidentId) return;

    const invId = getSingleInvestigationId({
      incidentId: selectedIncidentId,
      incidentReportNo,
    });

    try {
      if (!invId) {
        setInvestigations([]);
        return;
      }

      const invResp = await apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}/investigations/${encodeURIComponent(invId)}`);
      const invItem = invResp?.item || invResp || null;

      setInvestigations(invItem ? [invItem] : []);
      flashToast("Investigation refreshed");
    } catch (e) {
      setInvestigations([]);
      flashToast("No investigation yet");
    }
  }

async function createInvestigation() {
  if (!selectedIncidentId) return;
  setGlobalError("");

  const invId = getSingleInvestigationId({
    incidentId: selectedIncidentId,
    incidentReportNo,
  });

  if (!invId) {
    setGlobalError("Cannot create investigation: missing Incident Report No / IncidentId.");
    return;
  }

  // ✅ If already loaded, just open it
  const existing = (investigations || []).find((x) => x?.investigationId === invId && !x?.isDeleted);
  if (existing) {
    await selectInvestigation(invId);
    flashToast("Investigation opened");
    return;
  }

  // Build investigationData (your existing logic)
  let investigationData;

  if (isReiriIncident) {
    const affiliate = String(getReadableValue(incidentDataObj, "affiliate") || "").trim();
    const customerFromIncident = String(incidentDataObj?.customer || incidentDataObj?.endCustomer || "").trim();

    investigationData = buildReiriInvestigationTemplate({
      affiliate,
      customer: customerFromIncident,
      incidentId: selectedIncidentId,
      incidentReportNo,
    });

    const prefill = getReiriPrefillFromAffiliateResponse(customerResponseObj);
    if (prefill) {
      investigationData.report = investigationData.report || {};

      if (!String(investigationData.report.customer || "").trim() && prefill.customer) {
        investigationData.report.customer = prefill.customer;
      }
      if (!String(investigationData.report.reportBy || "").trim() && prefill.reportedBy) {
        investigationData.report.reportBy = prefill.reportedBy;
      }
      if (!String(investigationData.report.controllerSerialNumber || "").trim() && prefill.controllerSerialNumber) {
        investigationData.report.controllerSerialNumber = prefill.controllerSerialNumber;
      }
      if (!String(investigationData.report.softwareVersion || "").trim() && prefill.softwareVersion) {
        investigationData.report.softwareVersion = prefill.softwareVersion;
      }
    }

    investigationData.meta = {
      ...(investigationData.meta || {}),
      createdBySub: currentUserSub || undefined,
      clientCreatedAt: new Date().toISOString(),
    };
  } else {
    investigationData = {
      actionTaken: "",
      notes: "",
      meta: {
        clientCreatedAt: new Date().toISOString(),
        createdBySub: currentUserSub || undefined,
        incidentId: selectedIncidentId,
        productType: incidentProduct || "Unknown",
      },
    };
  }

  setBusy(true);
  try {
    // ✅ First: check if it exists (avoid creating duplicates if backend POST isn't fixed-id)
    try {
      const invResp = await apiFetch(
        `/incidents/${encodeURIComponent(selectedIncidentId)}/investigations/${encodeURIComponent(invId)}`
      );
      const invItem = invResp?.item || invResp || null;
      if (invItem?.investigationId) {
        setInvestigations([invItem]);
        await selectInvestigation(invItem.investigationId);
        flashToast("Investigation opened");
        return;
      }
    } catch {
      // Not found → create below
    }

    // ✅ Best: create/replace at fixed id (requires backend support)
    let createdId = "";

    try {
      await apiFetch(
        `/incidents/${encodeURIComponent(selectedIncidentId)}/investigations/${encodeURIComponent(invId)}`,
        { method: "PUT", body: { investigationData } }
      );
      createdId = invId;
    } catch (e) {
      const msg = e?.message || String(e);

      // Fallback: POST (ONLY safe if backend enforces invId = {reportNo}-INV)
      await apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}/investigations`, {
        method: "POST",
        body: { investigationData },
      });

      createdId = invId; // we still expect fixed id from backend
      if (process.env.NODE_ENV === "development") {
        console.warn("PUT not supported; fell back to POST:", msg);
      }
    }

    await refreshInvestigations();
    await selectInvestigation(createdId);
    flashToast("Investigation ready");
  } catch (e) {
    setGlobalError(e.message || String(e));
  } finally {
    setBusy(false);
  }
}


async function selectInvestigation(invId) {
  if (!selectedIncidentId) return;
  setGlobalError("");
  setSelectedInvestigationId(invId);
  setInvAttachments([]);
  setUploadProgress("");
  setUploadFile(null);

  try {
    const [invResp, atts] = await Promise.all([
      apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}/investigations/${encodeURIComponent(invId)}`),
      apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}/investigations/${encodeURIComponent(invId)}/attachments`),
    ]);

    // ✅ FIX: investigations_get returns { item: inv }
    const invItem = invResp?.item || invResp || null;

    setInvestigationDetails(invItem || null);
    setInvAttachments(atts.items || atts.attachments || []);

    const raw = invItem?.investigationData || {};

    // ✅ Determine Reiri based on investigation itself (schema/productType), not incident only
    const looksReiri =
      raw?.schema === "reiri-investigation-v1" ||
      raw?.productType === "Reiri" ||
      raw?.meta?.productType === "Reiri" ||
      isReiriIncident; // last fallback

    setIsReiriInv(!!looksReiri);

    const normalized = looksReiri
      ? normalizeReiriInvestigation(raw, {
          affiliate: String(getReadableValue(incidentDataObj, "affiliate") || "").trim(),
          customer: String(incidentDataObj?.customer || incidentDataObj?.endCustomer || "").trim(),
          incidentId: selectedIncidentId,
          incidentReportNo,
        })
      : raw;

    if (looksReiri && incidentReportNo) {
      normalized.report = normalized.report || {};
      normalized.report.incidentReportNo = incidentReportNo;
    }

    const sigOwnerSub = deepGet(normalized, "report.signatureOwnerSub", "");

    // New: treat as "my signature" if it was signed by me AND has either profile key or attachment id
    const hasAnySig =
      !!String(deepGet(normalized, "report.signatureS3Key", "") || "").trim() ||
      !!String(deepGet(normalized, "report.signatureAttachmentId", "") || "").trim();

    setUseMySignature(!!sigOwnerSub && sigOwnerSub === currentUserSub && hasAnySig);

    // ----------------------------

    setInvDraftObj(normalized);
    setEditInvestigationJson(JSON.stringify(normalized || {}, null, 2));

    // ✅ Editor mode based on the selected investigation type
    setInvEditorMode(looksReiri ? "form" : "json");

    flashToast("Investigation loaded");
  } catch (e) {
    const msg = e?.message || String(e);

    // ✅ If deleted investigation is requested, clear selection cleanly
    if (msg.includes("404")) {
      setSelectedInvestigationId(null);
      setInvestigationDetails(null);
      setInvAttachments([]);
      setEditInvestigationJson("{}");
      setInvDraftObj(null);
      setInvEditorMode("form");
      setIsReiriInv(false);
      flashToast("Investigation not found (deleted)");
      return;
    }

    setGlobalError(msg);
  }
}

  async function refreshSelectedInvestigation() {
    if (!selectedIncidentId || !selectedInvestigationId) return;
    await selectInvestigation(selectedInvestigationId);
  }

  function syncDraftToJson(nextObj) {
    setInvDraftObj(nextObj);
    setEditInvestigationJson(JSON.stringify(nextObj || {}, null, 2));
  }

  function setInvField(path, value) {
    const base = invDraftObj && typeof invDraftObj === "object" ? invDraftObj : {};
    const next = deepSet(base, path, value);
    syncDraftToJson(next);
  }

async function patchInvestigation() {
    if (!selectedIncidentId || !selectedInvestigationId) return;
    setGlobalError("");

    let obj;

    // ✅ use isReiriInv, not isReiriIncident
    if (isReiriInv && invEditorMode === "form") {
      obj = invDraftObj;

      // ✅ enforce required dropdown selections
      const missing = validateReiriRequiredSelections(obj);
      if (missing.length) {
        setGlobalError("Please complete all required selections before saving:\n\n- " + missing.join("\n- "));
        return;
      }

      if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
        setGlobalError("Edit Investigation: investigationData must be an object");
        return;
      }

      obj = normalizeReiriInvestigation(obj, {
        affiliate: String(getReadableValue(incidentDataObj, "affiliate") || "").trim(),
        customer: String(incidentDataObj?.customer || incidentDataObj?.endCustomer || "").trim(),
        incidentId: selectedIncidentId,
        incidentReportNo,
      });
    } else {
      obj = safeJsonParse(editInvestigationJson, null);
      if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
        setGlobalError("Edit Investigation: investigationData must be a JSON object");
        return;
      }
      if (isReiriInv) {
        obj = normalizeReiriInvestigation(obj, {
          affiliate: String(getReadableValue(incidentDataObj, "affiliate") || "").trim(),
          customer: String(incidentDataObj?.customer || incidentDataObj?.endCustomer || "").trim(),
          incidentId: selectedIncidentId,
          incidentReportNo,
        });
      }
    }

    // --- NEW LOGIC: Stamp Metadata and Handle Signatures ---
    const nowSec = Math.floor(Date.now() / 1000);
    // Always stamp "last edited"
    obj.meta = {
      ...(obj.meta || {}),
      lastEditedAt: nowSec,
      lastEditedBySub: currentUserSub || "",
      lastEditedBy: currentUserLabel || currentUserSub || "",
    };

    // Signature behavior (Reiri only here, since you only render signature UI for Reiri)
    if (isReiriInv) {
      obj.report = obj.report || {};

      if (useMySignature) {
        // ✅ Use profile signature instead of attachments
        const sig = profileSignature;

        if (!sig || !sig.key) {
          setGlobalError(
            "No profile signature found. Go to Profile → upload your signature, then come back and tick 'Use my signature'."
          );
          return;
        }

        // IMPORTANT: align this key name with what your PDF lambda reads
        obj.report.signatureS3Key = sig.key;

        // Optional: stop using attachment signature completely
        obj.report.signatureAttachmentId = "";

        obj.report.signatureOwnerSub = currentUserSub || "";
        obj.report.signatureOwner = currentUserLabel || currentUserSub || "";
        obj.report.signatureSignedAt = nowSec;
      } else {
        // ✅ not signing => wipe any existing signature so others don't “reuse” it
        obj.report.signatureS3Key = "";
        obj.report.signatureAttachmentId = "";
        obj.report.signatureOwnerSub = "";
        obj.report.signatureOwner = "";
        obj.report.signatureSignedAt = null;
      }
    }

    // -----------------------------------------------------

    setBusy(true);
    try {
      await apiFetch(
        `/incidents/${encodeURIComponent(selectedIncidentId)}/investigations/${encodeURIComponent(selectedInvestigationId)}`,
        {
          method: "PATCH",
          body: { investigationData: obj },
        }
      );
      await refreshSelectedInvestigation();
      await refreshInvestigations();
      flashToast("Investigation saved");
    } catch (e) {
      setGlobalError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function softDeleteInvestigation(invId) {
    if (!selectedIncidentId || !invId) return;

    const inv = investigations.find((x) => x.investigationId === invId) || null;
    const label = inv?.investigationNo || invId;

    const ok = window.confirm(`Delete this investigation log?\n\n${label}\n\nPress OK to confirm`);
    if (!ok) return;

    setBusy(true);
    setGlobalError("");
    try {
      await apiFetch(`/incidents/${encodeURIComponent(selectedIncidentId)}/investigations/${encodeURIComponent(invId)}`, {
        method: "PATCH",
        body: { action: "SOFT_DELETE" },
      });

      // ✅ Optimistic UI update (hide immediately)
      setInvestigations((prev) =>
        (prev || []).map((x) =>
          x.investigationId === invId ? { ...x, isDeleted: true, deletedAt: Math.floor(Date.now() / 1000) } : x
        )
      );

      // If currently selected, clear right panel
      if (selectedInvestigationId === invId) {
        setSelectedInvestigationId(null);
        setInvestigationDetails(null);
        setInvAttachments([]);
        setEditInvestigationJson("{}");
        setInvDraftObj(null);
        setInvEditorMode("form");
        setIsReiriInv(false);
      }

      flashToast("Investigation deleted");
      await refreshInvestigations(); // keep consistent with server filtering
    } catch (e) {
      setGlobalError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function listInvAttachments() {
    if (!selectedIncidentId || !selectedInvestigationId) return;
    try {
      const atts = await apiFetch(
        `/incidents/${encodeURIComponent(selectedIncidentId)}/investigations/${encodeURIComponent(selectedInvestigationId)}/attachments`
      );
      setInvAttachments(atts.items || atts.attachments || []);
      flashToast("Attachments refreshed");
    } catch (e) {
      setGlobalError(e.message || String(e));
    }
  }

  async function uploadInvAttachment() {
    if (!selectedIncidentId || !selectedInvestigationId) return;
    if (!uploadFile) {
      setGlobalError("Please choose a file first.");
      return;
    }

    setBusy(true);
    setGlobalError("");
    setUploadProgress("1/3 Requesting upload URL...");

    try {
      const presign = await apiFetch(
        `/incidents/${encodeURIComponent(selectedIncidentId)}/investigations/${encodeURIComponent(selectedInvestigationId)}/attachments/presign`,
        {
          method: "POST",
          body: {
            filename: uploadFile.name,
            contentType: uploadFile.type || "application/octet-stream",
            size: uploadFile.size,
          },
        }
      );

      const uploadUrl = presign.uploadUrl;
      const attachmentId = presign.attachmentId;
      if (!uploadUrl || !attachmentId) throw new Error("Presign did not return uploadUrl/attachmentId");

      setUploadProgress("2/3 Uploading to S3...");
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": uploadFile.type || "application/octet-stream" },
        body: uploadFile,
      });

      if (!putRes.ok) {
        const t = await putRes.text().catch(() => "");
        throw new Error(`S3 upload failed: HTTP ${putRes.status} ${t}`);
      }

      const etag = putRes.headers.get("etag");
      setUploadProgress("3/3 Confirming upload...");

      await apiFetch(
        `/incidents/${encodeURIComponent(selectedIncidentId)}/investigations/${encodeURIComponent(selectedInvestigationId)}/attachments/confirm`,
        { method: "POST", body: { attachmentId, etag: etag || undefined } }
      );

      setUploadProgress("Done ✅");
      setUploadFile(null);
      await listInvAttachments();
      flashToast("Uploaded");
    } catch (e) {
      setGlobalError(e.message || String(e));
      setUploadProgress("");
    } finally {
      setBusy(false);
    }
  }

  async function downloadInvAttachment(att) {
    if (!selectedIncidentId || !selectedInvestigationId) return;
    if (!att?.attachmentId) return;

    setBusy(true);
    setGlobalError("");
    try {
      const data = await apiFetch(
        `/incidents/${encodeURIComponent(selectedIncidentId)}/investigations/${encodeURIComponent(selectedInvestigationId)}/attachments/download`,
        { method: "POST", body: { attachmentId: att.attachmentId } }
      );

      const url = data.downloadUrl || data.url;
      if (!url) throw new Error("No download URL returned");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setGlobalError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function deleteInvAttachment(att) {
    if (!selectedIncidentId || !selectedInvestigationId) return;
    if (!att?.attachmentId) return;

    const ok = window.confirm(`Delete attachment "${att.filename}"? (soft delete)`);
    if (!ok) return;

    setBusy(true);
    setGlobalError("");
    try {
      await apiFetch(
        `/incidents/${encodeURIComponent(selectedIncidentId)}/investigations/${encodeURIComponent(selectedInvestigationId)}/attachments/delete`,
        {
          method: "POST",
          body: { attachmentId: att.attachmentId, deleteS3: false },
        }
      );
      await listInvAttachments();
      flashToast("Deleted");
    } catch (e) {
      setGlobalError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  /* -------------------------
     Derived (list + archive grouping)
  ------------------------- */

  const visibleIncidents = useMemo(() => {
    let list = Array.isArray(incidents) ? [...incidents] : [];

    if (myOnly && currentUserSub) {
      list = list.filter((inc) => getIncidentOwnerSub(inc) === currentUserSub);
    }

    if (searchTerm.trim()) {
      list = list.filter((inc) => matchesSearch(inc, searchTerm));
    }

    list.sort((a, b) => {
      if (sortUpdated === "updated_asc" || sortUpdated === "updated_desc") {
        const av = Number(a?.updatedAt || 0);
        const bv = Number(b?.updatedAt || 0);
        return sortUpdated === "updated_asc" ? av - bv : bv - av;
      }

      if (sortUpdated === "id_asc" || sortUpdated === "id_desc") {
        const A = parseIncidentIdParts(a?.reportNo || a?.incidentData?.reportNo || a?.incidentId);
        const B = parseIncidentIdParts(b?.reportNo || b?.incidentData?.reportNo || b?.incidentId);

        const byYear = A.year - B.year;
        if (byYear !== 0) return sortUpdated === "id_asc" ? byYear : -byYear;

        const bySeq = A.seq - B.seq;
        if (bySeq !== 0) return sortUpdated === "id_asc" ? bySeq : -bySeq;

        const byPrefix = A.prefix.localeCompare(B.prefix);
        if (byPrefix !== 0) return byPrefix;

        const aId = (a?.reportNo || a?.incidentData?.reportNo || a?.incidentId || "").toString();
        const bId = (b?.reportNo || b?.incidentData?.reportNo || b?.incidentId || "").toString();
        return aId.localeCompare(bId);
      }

      const av = Number(a?.updatedAt || 0);
      const bv = Number(b?.updatedAt || 0);
      return bv - av;
    });

    return list;
  }, [incidents, myOnly, currentUserSub, searchTerm, sortUpdated]);

  const archiveGroups = useMemo(() => {
    if (!isArchiveMode) return [];

    const list = visibleIncidents;
    const map = new Map();

    for (const inc of list) {
      const p = getIncidentProduct(inc);
      const arr = map.get(p) || [];
      arr.push(inc);
      map.set(p, arr);
    }

    const known = PRODUCT_OPTIONS.slice();
    const groups = [];

    for (const p of known) {
      const arr = map.get(p);
      if (arr && arr.length) groups.push({ product: p, items: arr });
    }

    const others = [];
    for (const [p, items] of map.entries()) {
      if (!known.includes(p)) others.push({ product: p, items });
    }
    others.sort((a, b) => a.product.localeCompare(b.product));
    groups.push(...others);

    // ✅ Apply the same sorting choice to archived items too
    for (const g of groups) {
      g.items = sortIncidents(g.items, sortUpdated);
    }

    return groups;
  }, [isArchiveMode, visibleIncidents, sortUpdated]);

  const selectedArchiveGroup = useMemo(() => {
    if (!isArchiveMode) return null;
    if (!selectedArchiveProduct) return null;
    return archiveGroups.find((g) => g.product === selectedArchiveProduct) || null;
  }, [isArchiveMode, archiveGroups, selectedArchiveProduct]);

  const incidentStatusRaw = incidentDetails?.status || selectedIncident?.status || "-";
  const incidentStatus = STATUS_LABELS[String(incidentStatusRaw || "").toUpperCase()] || incidentStatusRaw;

  const statusUpper = String(incidentStatusRaw || "").toUpperCase();
  const canClose = selectedIncidentId && statusUpper !== "CLOSED" && statusUpper !== "ARCHIVED" && statusUpper !== "DELETED";

  const warrantyEnabled =
  String(incidentDetails?.status || "").toUpperCase() === "ARCHIVED" &&
  !!incidentDetails?.archivePdfKey;


  const inArchivedFilter = statusFilter === "ARCHIVED";
  const canArchive = selectedIncidentId && !inArchivedFilter && statusUpper !== "ARCHIVED" && statusUpper !== "DELETED";

  const canDelete = selectedIncidentId && statusUpper !== "DELETED";

  const visibleCustomerAttachments = useMemo(() => {
    const list = Array.isArray(custAttachments) ? custAttachments : [];
    if (custShowDeleted) return list;
    return list.filter((a) => (a?.status || "").toUpperCase() !== "DELETED");
  }, [custAttachments, custShowDeleted]);

  const nowSec = Math.floor(Date.now() / 1000);
  const linkExpiresAt = Number(customerLink?.expiresAt || 0);
  const isLinkExpired = !!linkExpiresAt && linkExpiresAt <= nowSec;
  const isLinkDisabled = !!customerLink?.isDisabled;

  const srNowSec = Math.floor(Date.now() / 1000);
const srExpiresAt = Number(serviceRequestLink?.expiresAt || 0);
const isSrExpired = !!srExpiresAt && srExpiresAt <= srNowSec;
const isSrDisabled = !!serviceRequestLink?.isDisabled;

const serviceRequestUrl = useMemo(() => {
  const stored = serviceRequestLink?.url;
  if (isValidAbsoluteHttpUrl(stored)) return stored;

  const token = serviceRequestLink?.token;
  if (!token || !selectedIncidentId) return "";

  return buildServiceRequestUrl({ incidentId: selectedIncidentId, token });
}, [serviceRequestLink?.url, serviceRequestLink?.token, selectedIncidentId]);


  // ✅ Compute customerUrl safely:
  // - Trust customerLink.url only if valid absolute http(s)
  // - Otherwise rebuild from env base + token
  const customerUrl = useMemo(() => {
    const stored = customerLink?.url;
    if (isValidAbsoluteHttpUrl(stored)) return stored;

    const token = customerLink?.token;
    if (!token || !selectedIncidentId) return "";

    return buildCustomerUrl({ incidentId: selectedIncidentId, token });
  }, [customerLink?.url, customerLink?.token, selectedIncidentId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setCustomerHtml("");
        if (!selectedIncidentId) return;

        const raw = getCustomerRawHtml(customerResponseObj);
        if (!raw) return;

        setCustomerHtmlLoading(true);

        const safe = sanitizeCustomerHtml(raw);
        const withImgs = await presignImagesInHtml({ incidentId: selectedIncidentId, html: safe });

        if (!cancelled) setCustomerHtml(withImgs);
      } catch (e) {
        console.error("build customerHtml failed:", e);
        if (!cancelled) setCustomerHtml("");
      } finally {
        if (!cancelled) setCustomerHtmlLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedIncidentId, customerResponseObj]);

  async function refreshCustomerInlineImages() {
    if (!selectedIncidentId) return;

    const raw = getCustomerRawHtml(customerResponseObj);
    if (!raw) return;

    setCustomerHtmlLoading(true);
    try {
      const safe = sanitizeCustomerHtml(raw);
      const withImgs = await presignImagesInHtml({ incidentId: selectedIncidentId, html: safe });
      setCustomerHtml(withImgs);
      flashToast("Images refreshed");
    } catch (e) {
      console.error("refreshCustomerInlineImages failed:", e);
      setGlobalError(e.message || String(e));
    } finally {
      setCustomerHtmlLoading(false);
    }
  }

  const signatureOptions = useMemo(() => {
    const list = Array.isArray(invAttachments) ? invAttachments : [];
    const filtered = list.filter((a) => (a?.status || "").toUpperCase() !== "DELETED");
    return filtered.map((a) => ({
      value: a.attachmentId,
      label: `${a.filename || "(no filename)"} — ${a.attachmentId}`,
    }));
  }, [invAttachments]);

  /* ===========================
     UI
  =========================== */

return (
  <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#f8fafc", fontFamily: "'Inter', sans-serif" }}>
    <Toast message={toast} />

    {/* TOP BAR */}
    <div
      style={{
        height: 72,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 20px",
        borderBottom: "1px solid #e2e8f0",
        background: "white",
        flexShrink: 0,
        gap: 12,
      }}
    >
      {/* Left: Logo */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 220 }}>
        <img
          src={logo}
          alt="Company Logo"
          style={{ width: 52, height: 52, objectFit: "contain" }}
        />
      </div>

      {/* Center: Title */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "center" }}>
        <div
          style={{
            fontWeight: 800,
            fontSize: 18,
            color: "#1e293b",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Incident &amp; Investigation Dashboard
        </div>
      </div>

      {/* Right: Actions */}
{/* Right: Actions */}
<div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 220, justifyContent: "flex-end", flexWrap: "wrap" }}>

  {mainView === "profile" ? (
    <SecondaryButton disabled={busy} onClick={closeProfile} title="Back">
      ← Back
    </SecondaryButton>
  ) : (
    <SecondaryButton disabled={busy} onClick={openProfile} title="Open Profile">
      Profile
    </SecondaryButton>
  )}

  {mainView === "warrantyList" ? (
    <SecondaryButton disabled={busy} onClick={closeWarrantyList} title="Back">
      ← Back
    </SecondaryButton>
  ) : (
    <SecondaryButton disabled={busy} onClick={openWarrantyList} title="Open Warranty List">
      Warranty List
    </SecondaryButton>
  )}

{mainView === "incidentList" ? (
  <SecondaryButton disabled={busy} onClick={closeIncidentList} title="Back">
    ← Back
  </SecondaryButton>
) : (
  <SecondaryButton disabled={busy} onClick={openIncidentList} title="Open Incident List">
    Incident List
  </SecondaryButton>
)}
  
  <PrimaryButton disabled={busy} onClick={handleLogout} title="Log out">
    Logout
  </PrimaryButton>
</div>

    </div>

    {/* BODY */}
    <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
      {/* LEFT SIDEBAR */}
      <div style={{ width: 380, display: "flex", flexDirection: "column", borderRight: "1px solid #e2e8f0", background: "#f1f5f9" }}>
<div style={{ padding: "20px", borderBottom: "1px solid #e2e8f0", background: "white" }}>
  <select
    value={statusFilter}
    onChange={(e) => setStatusFilter(e.target.value)}
    disabled={busy}
    style={{ width: "100%", padding: "10px", borderRadius: 8, border: "1px solid #e2e8f0", marginBottom: 12 }}
  >
    {STATUSES.map((s) => (
      <option key={s} value={s}>
        {STATUS_LABELS[s] || s}
      </option>
    ))}
  </select>

  <input
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    placeholder={isArchiveMode ? "Search archived (affiliate, subject, ID, product)..." : "Search affiliate, subject, ID..."}
    disabled={busy}
    style={{ width: "100%", padding: "10px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
  />





          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, fontSize: 12, fontWeight: 600, gap: 10 }}>
            <label
              style={{ display: "flex", alignItems: "center", gap: 6, cursor: busy ? "not-allowed" : "pointer", color: "#334155" }}
              title={currentUserSub ? "" : "User id not available yet (sign out/in if needed)"}
            >
              <input type="checkbox" checked={myOnly} onChange={(e) => setMyOnly(e.target.checked)} disabled={busy || !currentUserSub} />
              User Created
            </label>

            <select
              value={sortUpdated}
              onChange={(e) => setSortUpdated(e.target.value)}
              disabled={busy}
              style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12, background: "white" }}
              title="Sort"
            >
              <option value="updated_desc">Updated (newest)</option>
              <option value="updated_asc">Updated (oldest)</option>
              <option value="id_asc">Incident ID (0001 → 9999)</option>
              <option value="id_desc">Incident ID (9999 → 0001)</option>
            </select>

            <div style={{ color: "#64748b", whiteSpace: "nowrap" }}>{isArchiveMode ? `${visibleIncidents.length} Archived` : `${visibleIncidents.length} Found`}</div>
          </div>

          {(searchTerm || myOnly) && (
            <div style={{ marginTop: 10 }}>
              <SecondaryButton
                disabled={busy}
                onClick={() => {
                  setSearchTerm("");
                  setMyOnly(false);
                }}
              >
                Clear Filters
              </SecondaryButton>
            </div>
          )}
        </div>
        

        {/* Scrollable List */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px" }}>
          {loadingIncidents ? (
            <div style={{ textAlign: "center", padding: 20, color: "#64748b" }}>Loading...</div>
          ) : incidents.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: "#64748b" }}>No incidents found.</div>
          ) : visibleIncidents.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: "#64748b" }}>No incidents match filters.</div>
          ) : isArchiveMode ? (
            <>
              <div style={{ padding: "8px 10px", color: "#64748b", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>Products</div>
              {archiveGroups.length === 0 ? (
                <div style={{ textAlign: "center", padding: 20, color: "#64748b" }}>No archived incidents to group.</div>
              ) : (
                archiveGroups.map((g) => {
                  const active = selectedArchiveProduct === g.product;
                  return (
                    <div
                      key={g.product}
                      onClick={() => {
                        setSelectedArchiveProduct(g.product);
                        setSelectedIncidentId(null);
                        setIncidentDetails(null);
                      }}
                      style={{
                        padding: "12px 16px",
                        borderRadius: 10,
                        cursor: "pointer",
                        marginBottom: 8,
                        transition: "0.2s",
                        background: active ? "white" : "transparent",
                        boxShadow: active ? "0 4px 12px rgba(0,0,0,0.05)" : "none",
                        position: "relative",
                        border: active ? "1px solid #e2e8f0" : "1px solid transparent",
                      }}
                    >
                      {active && (
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            top: "20%",
                            bottom: "20%",
                            width: 3,
                            background: "#1e293b",
                            borderRadius: "0 4px 4px 0",
                          }}
                        />
                      )}
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, gap: 10 }}>
                        <div style={{ fontWeight: 800, fontSize: 13, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {g.product}
                        </div>
                        <Pill status="ARCHIVED">{g.items.length}</Pill>
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>Click to view archived list</div>
                    </div>
                  );
                })
              )}
            </>
          ) : (
            visibleIncidents.map((inc) => {
              const active = selectedIncidentId === inc.incidentId;
              const label = inc?.incidentData?.affiliate || inc?.incidentData?.customerName || inc?.incidentId;
              const stRaw = String(inc?.status || "").toUpperCase();
              const stLabel = STATUS_LABELS[stRaw] || inc.status || "-";

              return (
                <div
                  key={inc.incidentId}
                  onClick={() => selectIncident(inc.incidentId)}
                  style={{
                    padding: "12px 16px",
                    borderRadius: 10,
                    cursor: "pointer",
                    marginBottom: 8,
                    transition: "0.2s",
                    background: active ? "white" : "transparent",
                    boxShadow: active ? "0 4px 12px rgba(0,0,0,0.05)" : "none",
                    position: "relative",
                    border: active ? "1px solid #e2e8f0" : "1px solid transparent",
                  }}
                >
                  {active && (
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: "20%",
                        bottom: "20%",
                        width: 3,
                        background: "#1e293b",
                        borderRadius: "0 4px 4px 0",
                      }}
                    />
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, gap: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {label}
                    </div>
                    <Pill status={inc.status}>{stLabel}</Pill>
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    {inc.reportNo || inc.incidentData?.reportNo || inc.incidentId} · {prettyTime(inc.updatedAt)}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Sidebar Footer - Create */}
        <div style={{ padding: 20, borderTop: "1px solid #e2e8f0", background: "white", display: "flex", gap: 10 }}>
          <PrimaryButton
            disabled={busy}
            onClick={() => {
              setMainView("create");
              setSelectedIncidentId(null);
              setIncidentDetails(null);
              setInvestigations([]);
              setSelectedInvestigationId(null);
              setInvestigationDetails(null);
              setInvAttachments([]);
              setCustomerLink(null);
              setCustAttachments([]);
              setCustShowDeleted(false);
              setActiveTab("Overview");
              setGlobalError("");

              setCreateAffiliate("");
              setCreateProduct("");
              setCreateZendeskTicketNumber("");
              setCreateSubject("");

              setSelectedArchiveProduct("");

              setEditInvestigationJson("{}");
              setInvDraftObj(null);
              setInvEditorMode("form");
              setIsReiriInv(false);
            }}
          >
            + New Incident
          </PrimaryButton>
            <SecondaryButton
    onClick={loadIncidents}
    disabled={loadingIncidents || busy}
    title="Refresh incident list"
  >
    {loadingIncidents ? "..." : "↻"}
  </SecondaryButton>
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", background: "white" }}>
        {mainView === "warrantyList" ? (
          <div style={{ padding: 32, overflowY: "auto", flex: 1 }}>
            <WarrantyList />
          </div>
        ) : null}

        {mainView === "incidentList" ? (
  <div style={{ padding: 32, overflowY: "auto", flex: 1 }}>
    <IncidentList
      onClose={closeIncidentList}
      onOpenIncident={(incidentId) => {
        setMainView("detail");
        selectIncident(incidentId);
      }}
    />
  </div>
) : null}

        {mainView === "profile" ? (
  <div style={{ padding: 32, overflowY: "auto", flex: 1 }}>
    <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
      <h1 style={{ margin: 0, color: "#1e293b" }}>My Profile</h1>
    </div>

    <div style={{ maxWidth: 900 }}>
  {/* ✅ NEW: Display Name card goes here (ABOVE Signature) */}
  <Card title="Profile">
    <div style={{ color: "#64748b", fontSize: 13 }}>
      Enter your display name and click 'Save', this name will be used in reports automatically.
    </div>

    <div style={{ marginTop: 12, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      <label style={{ fontSize: 12, color: "#64748b", fontWeight: 700, width: 110 }}>
        Display Name
      </label>

      <input
        value={displayName}
        disabled={busy || profileSaveBusy || profileLoading}
        onChange={(e) => setDisplayName(e.target.value)}
        placeholder="e.g. Nicholas Tan"
        style={{
          flex: 1,
          minWidth: 260,
          padding: 10,
          borderRadius: 10,
          border: "1px solid #e2e8f0",
        }}
      />

      <PrimaryButton
        disabled={busy || profileSaveBusy || profileLoading || !(displayName || "").trim()}
        onClick={saveDisplayName}
        title="Save display name"
      >
        {profileSaveBusy ? "Saving..." : "Save"}
      </PrimaryButton>
    </div>

    {profileSaveStatus ? (
      <div style={{ marginTop: 10, fontSize: 12, color: profileSaveStatus.includes("failed") ? "#991b1b" : "#64748b" }}>
        {profileSaveStatus}
      </div>
    ) : null}
  </Card>

<Card title="Signature">
  <div style={{ color: "#64748b", fontSize: 13 }}>
    Upload your signature once. In each investigation, tick ‘Use my signature’ before saving to include it in PDFs.
  </div>

{/* ✅ Existing signature status */}
<div
  style={{
    marginTop: 12,
    padding: 12,
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    background: hasProfileSignature ? "#f0fdf4" : "#fef2f2",
    color: hasProfileSignature ? "#166534" : "#991b1b",
    fontSize: 12,
    fontWeight: 700,
  }}
>
  {hasProfileSignature ? (
    <div>
      <div style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>
        Signature on file ✅
      </div>

      <div style={{ marginTop: 8, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <SecondaryButton
          disabled={busy || sigPreviewBusy}
          onClick={loadSignaturePreview}
          title="Refresh preview"
        >
          {sigPreviewBusy ? "Loading..." : "Refresh Preview"}
        </SecondaryButton>

        {sigPreviewErr ? (
          <span style={{ fontSize: 12, color: "#991b1b" }}>{sigPreviewErr}</span>
        ) : null}
      </div>

      {sigPreviewUrl ? (
<div style={{ marginTop: 12 }}>
  <img
    src={sigPreviewUrl}
    alt="Signature preview"
    style={{
      maxWidth: 360,
      width: "100%",
      maxHeight: 120,
      objectFit: "contain",
      background: "white",
      border: "1px solid #e2e8f0",
      borderRadius: 12,
      padding: 12,
      display: "block",
    }}
  />
</div>
      ) : (
        <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
          Preview not loaded yet.
        </div>
      )}
    </div>
  ) : (
    <div style={{ fontSize: 12, fontWeight: 800 }}>
      No signature on file ❌
    </div>
  )}
</div>


  <div
    style={{
      marginTop: 16,
      padding: 16,
      border: "2px dashed #cbd5e1",
      borderRadius: 12,
      background: "#f8fafc",
    }}
  >
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <input
        type="file"
        accept="image/png,image/jpeg"
        disabled={sigBusy || busy}
        onChange={(e) => setSigFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
      />

      <PrimaryButton
        disabled={sigBusy || busy || !sigFile}
        onClick={uploadProfileSignature}
        title="Upload signature"
      >
        {sigBusy ? "Uploading..." : "Upload Signature"}
      </PrimaryButton>
    </div>

    <div style={{ marginTop: 10, fontSize: 12, color: sigStatus.startsWith("Upload failed") ? "#991b1b" : "#64748b" }}>
      {sigStatus || "PNG/JPEG only."}
    </div>
  </div>
</Card>



      <div style={{ marginTop: 16 }}>
        <Card title="Account">
          <DetailRow label="Email" value={currentUserLabel || "-"} />
          <DetailRow label="User Sub" value={currentUserSub || "-"} />
        </Card>
      </div>
    </div>
  </div>
) : null}

        {/* Empty state (normal mode) */}
        {mainView === "detail" && !selectedIncidentId && !isArchiveMode ? (
          <div style={{ margin: "auto", textAlign: "center", color: "#94a3b8", padding: 24 }}>
            <h2 style={{ margin: 0, color: "#64748b" }}>Select an incident to get started</h2>
            <p style={{ marginTop: 10 }}>Use the sidebar to filter and browse cases.</p>
          </div>
        ) : null}

        {/* ✅ Archive Mode Main Panel */}
        {mainView === "detail" && isArchiveMode && !selectedIncidentId ? (
          <div style={{ padding: 32, overflowY: "auto", flex: 1 }}>
            {!selectedArchiveProduct ? (
              <div style={{ margin: "auto", textAlign: "center", color: "#94a3b8", padding: 24 }}>
                <h2 style={{ margin: 0, color: "#64748b" }}>Select a product</h2>
                <p style={{ marginTop: 10 }}>Choose a product from the left sidebar to view archived incidents.</p>
              </div>
            ) : !selectedArchiveGroup || !selectedArchiveGroup.items?.length ? (
              <div style={{ margin: "auto", textAlign: "center", color: "#94a3b8", padding: 24 }}>
                <h2 style={{ margin: 0, color: "#64748b" }}>No archived incidents</h2>
                <p style={{ marginTop: 10 }}>No archived incidents found for {selectedArchiveProduct}.</p>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
                  <h2 style={{ margin: 0, color: "#1e293b" }}>Archived Dashboard — {selectedArchiveProduct}</h2>
                  <div style={{ color: "#64748b", fontWeight: 700 }}>{selectedArchiveGroup.items.length} incidents</div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {selectedArchiveGroup.items.map((inc) => {
                    const label =
                      inc?.incidentData?.affiliate ||
                      inc?.incidentData?.customerName ||
                      inc?.reportNo ||
                      inc?.incidentData?.reportNo ||
                      inc?.incidentId;

                    const subj = inc?.incidentData?.subject || inc?.incidentData?.issue || "";
                    return (
                      <div
                        key={inc.incidentId}
                        onClick={() => selectIncident(inc.incidentId)}
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 12,
                          padding: 14,
                          cursor: "pointer",
                          background: "white",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                          <div style={{ fontWeight: 800, color: "#1e293b" }}>{label}</div>
                          <Pill status="ARCHIVED">Archived</Pill>
                        </div>
                        <div style={{ marginTop: 6, color: "#64748b", fontSize: 12 }}>
                          {(inc.reportNo || inc.incidentData?.reportNo || inc.incidentId)} · {prettyTime(inc.archivedAt || inc.updatedAt)}
                        </div>

                        {subj ? <div style={{ marginTop: 8, color: "#334155", fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}>{subj}</div> : null}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        ) : null}

        {/* Create view */}
        {mainView === "create" ? (
          <div style={{ padding: 40, maxWidth: 900, width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <h1 style={{ margin: 0, color: "#1e293b" }}>Create Incident</h1>
              <div style={{ display: "flex", gap: 10 }}>
                <SecondaryButton
                  disabled={busy}
                  onClick={() => {
                    setCreateAffiliate("");
                    setCreateProduct("");
                    setCreateZendeskTicketNumber("");
                    setCreateSubject("");
                    setGlobalError("");
                  }}
                >
                  Clear
                </SecondaryButton>
                <PrimaryButton disabled={busy} onClick={createIncident}>
                  Submit
                </PrimaryButton>
              </div>
            </div>

            <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <SelectField label="Affiliate" required value={createAffiliate} onChange={(e) => setCreateAffiliate(e.target.value)} options={AFFILIATE_OPTIONS} disabled={busy} />
              <SelectField label="Product" required value={createProduct} onChange={(e) => setCreateProduct(e.target.value)} options={PRODUCT_OPTIONS} disabled={busy} />

              <TextField
                label="Zendesk Ticket Number"
                value={createZendeskTicketNumber}
                onChange={(e) => setCreateZendeskTicketNumber(e.target.value)}
                placeholder="e.g. 123"
                disabled={busy}
              />
              <div style={{ gridColumn: "1 / -1" }}>
                <TextAreaField
                  label="Subject"
                  required
                  value={createSubject}
                  onChange={(e) => setCreateSubject(e.target.value)}
                  placeholder="Short description of the case"
                  disabled={busy}
                  rows={5}
                />
              </div>
            </div>

            {globalError ? (
              <div style={{ marginTop: 14, padding: 16, background: "#fef2f2", color: "#991b1b", borderRadius: 12, fontWeight: 600 }}>{globalError}</div>
            ) : null}
          </div>
        ) : null}

        {/* Detail view */}
        {mainView === "detail" && selectedIncidentId ? (
          <>
            {/* Header */}
            <div
              style={{
                padding: "24px 32px",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#1e293b" }}>
                  {incidentDisplayId}
                  </h1>

                  <Pill status={incidentStatusRaw}>{incidentStatus}</Pill>
                </div>
                <div style={{ color: "#64748b", fontSize: 13, marginTop: 6 }}>
                  Created {prettyTime(incidentDetails?.createdAt || selectedIncident?.createdAt)} · Updated{" "}
                  {prettyTime(incidentDetails?.updatedAt || selectedIncident?.updatedAt)}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <IconButton disabled={busy || !selectedIncidentId} onClick={() => patchIncident({ status: "OPEN_TO_CUSTOMER" })} title="Set status to Open">
                  Open
                </IconButton>

                <IconButton disabled={busy || !canClose} onClick={closeCase} title={canClose ? "Close incident via /close endpoint" : "Already closed/archived/deleted"}>
                  Close
                </IconButton>

                <IconButton
                  disabled={busy || !canArchive}
                  onClick={archiveCase}
                  title={canArchive ? "Archive incident via /archive endpoint" : "Archive disabled in ARCHIVED filter / already archived/deleted"}
                >
                  Archive
                </IconButton>

                <DangerButton disabled={busy || !canDelete} onClick={deleteIncidentSoft} title={canDelete ? "Soft delete (hide from lists)" : "Already deleted"}>
                  Delete
                </DangerButton>

                <PrimaryButton disabled={busy} onClick={downloadArchivePdf} title="Download archive PDF">
                  PDF
                </PrimaryButton>

                <SecondaryButton disabled={busy} onClick={refreshSelectedIncident} title="Reload incident">
                  Reload
                </SecondaryButton>
              </div>
            </div>


{/* Tabs Bar */}
<div style={{ padding: "0 32px", borderBottom: "1px solid #f1f5f9", display: "flex", gap: 32 }}>
  {["Overview", "Affiliate", "Investigations", "Service Request", "Warranty"].map((t) => {
    const disabled = t === "Warranty" && !warrantyEnabled;

    return (
      <button
        key={t}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setActiveTab(t);
        }}
        style={{
          padding: "16px 0",
          border: "none",
          background: "none",
          fontSize: 14,
          fontWeight: 700,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.4 : 1,
          color: activeTab === t ? "#1e293b" : "#94a3b8",
          borderBottom: activeTab === t ? "2px solid #1e293b" : "2px solid transparent",
        }}
      >
        {t}
      </button>
    );
  })}
</div>


            {/* Scrollable Work Area */}
            <div style={{ flex: 1, overflowY: "auto", padding: "32px" }}>
              {globalError ? (
                <div style={{ padding: 16, background: "#fef2f2", color: "#991b1b", borderRadius: 12, marginBottom: 24, fontWeight: 600 }}>{globalError}</div>
              ) : null}

              {/* Overview */}
              {activeTab === "Overview" ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "start" }}>
<Card
  title="Detailed Information"
  right={
    isEditingIncident ? (
      <div style={{ display: "flex", gap: 10 }}>
        <SecondaryButton
          disabled={busy}
          onClick={() => {
            // revert local edits back to server
            setIncidentEditJson(JSON.stringify(incidentDetails?.incidentData || incidentDataObj || {}, null, 2));
            setIsEditingIncident(false);
            setGlobalError("");
          }}
        >
          Cancel
        </SecondaryButton>

        <PrimaryButton
          disabled={busy}
          onClick={() => {
            const obj = safeJsonParse(incidentEditJson, null);
            if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
              setGlobalError("Incident edit: incidentData must be a JSON object");
              return;
            }
            patchIncident({ incidentDataObj: obj, notificationEmail });
            setIsEditingIncident(false);
          }}
        >
          Save Changes
        </PrimaryButton>
      </div>
    ) : (
      <SecondaryButton
        disabled={busy}
        onClick={() => {
          // start editor from current server data
          setIncidentEditJson(JSON.stringify(incidentDetails?.incidentData || incidentDataObj || {}, null, 2));
          setIsEditingIncident(true);
        }}
      >
        Edit
      </SecondaryButton>
    )
  }
>

{!isEditingIncident ? (
  <>
{READABLE_FIELDS.map((f) => {
  // Custom render: Zendesk ticket as a link
  if (f.key === "zendeskTicketNumber") {
    var raw = getReadableValue(incidentDataObj, f.key);
    var ticketNo = normalizeZendeskTicketNumber(raw);
    var url = buildZendeskTicketUrl(ticketNo);

    return (
      <div key={f.key} style={{ padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>
          {f.label}
        </div>

        <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 600 }}>
          {ticketNo ? (
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", textDecoration: "underline" }}>
              {ticketNo}
            </a>
          ) : (
            <span style={{ fontWeight: 500, color: "#94a3b8" }}>-</span>
          )}
        </div>
      </div>
    );
  }

  // Default render for other fields
  return (
    <DetailRow
      key={f.key}
      label={f.label}
      value={valueToDisplay(getReadableValue(incidentDataObj, f.key))}
      multiline={f.multiline}
    />
  );
})}

  </>
) : (
  <CodeArea
    value={incidentEditJson}
    onChange={(e) => setIncidentEditJson(e.target.value)}
    rows={20}
    disabled={busy}
  />
)}



                  </Card>

                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <Card
                      title="Customer Link Management"
                      right={
                        <SecondaryButton disabled={busy} onClick={disableCustomerLink} title="Disable customer link">
                          Disable
                        </SecondaryButton>
                      }
                    >
                      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <label style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>Days</label>
                        <input
                          type="number"
                          min={1}
                          max={30}
                          value={genDays}
                          disabled={busy}
                          onChange={(e) => setGenDays(Math.max(1, Math.min(30, parseInt(e.target.value || "3", 10))))}
                          style={{ width: 72, padding: 8, borderRadius: 8, border: "1px solid #e2e8f0" }}
                        />

                        <PrimaryButton disabled={busy} onClick={generateOrReopenCustomerLink} title="Generate / reopen a new customer link">
                          Generate Link
                        </PrimaryButton>

                        <SecondaryButton
                          disabled={busy || !customerUrl || isLinkDisabled || isLinkExpired}
                          onClick={() => window.open(customerUrl, "_blank", "noopener,noreferrer")}
                          title={isLinkDisabled ? "Link disabled" : isLinkExpired ? "Link expired (regenerate)" : "Open customer page"}
                        >
                          Open
                        </SecondaryButton>
                      </div>

                      {customerLink ? (
                        <div style={{ marginTop: 16, fontSize: 12, background: "#f8fafc", padding: 12, borderRadius: 8 }}>

                          <div style={{ marginBottom: 8 }}>
                            <strong>Expires:</strong> {prettyTime(customerLink.expiresAt)} {customerLink.validDays ? `(${customerLink.validDays} days)` : ""}
                          </div>

                          {customerUrl ? (
                            <div>
                              <strong>URL:</strong> <code style={{ wordBreak: "break-all" }}>{customerUrl}</code>
                            </div>
                          ) : (
                            <div style={{ color: "#64748b" }}>
                              Generate link to open the Incident Report Form (public link).
                            </div>
                          )}

                          <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>

                            <SecondaryButton
                              disabled={busy || !customerUrl}
                              onClick={() => {
                                navigator.clipboard.writeText(customerUrl);
                                flashToast("URL copied");
                              }}
                              title={customerUrl ? "Copy URL" : "URL not available"}
                            >
                              Copy URL
                            </SecondaryButton>
                          </div>
                        </div>
                      ) : (
                        <div style={{ marginTop: 14, color: "#64748b", fontSize: 13 }}>Generate a token to open the customer form.</div>
                      )}
                      <div style={{ marginTop: 16, borderTop: "1px solid #e2e8f0", paddingTop: 12 }}>
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>
      Notification Email
    </label>

    <input
      type="email"
      value={notificationEmail}
      disabled={busy}
      onChange={(e) => setNotificationEmail(e.target.value)}
      placeholder="name@company.com"
      style={{
        width: "100%",
        padding: 10,
        borderRadius: 8,
        border: "1px solid #e2e8f0",
        fontSize: 14,
      }}
    />
<PrimaryButton
  disabled={busy}
  onClick={() => {
    const email = (notificationEmail || "").trim();

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      flashToast("Invalid Notification Email");
      return;
    }

    patchIncident({
      incidentDataObj:
        incidentDetails?.incidentData ||
        selectedIncident?.incidentData ||
        {},
      notificationEmail: email,
    });
  }}
>
  Save Notification Email
</PrimaryButton>


    <div style={{ fontSize: 12, color: "#64748b" }}>
      If provided, an email will be sent when the affiliate submits the Incident Report / Service Request.
    </div>
  </div>
</div>

                    </Card>
                  </div>
                </div>
              ) : null}

              {/* Affiliate */}
              {activeTab === "Affiliate" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                  <Card
                    title="Customer Response"
                    right={
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <Segmented
                          value={customerResponseViewMode}
                          onChange={setCustomerResponseViewMode}
                          options={[
                            { value: "readable", label: "Readable" },
                            { value: "json", label: "JSON" },
                          ]}
                          disabled={busy}
                        />
                        <SecondaryButton disabled={busy || !customerResponseObj || customerHtmlLoading} onClick={refreshCustomerInlineImages} title="Refresh inline images">
                          {customerHtmlLoading ? "..." : "Refresh Images"}
                        </SecondaryButton>
                      </div>
                    }
                  >
                    {customerResponseViewMode === "json" ? (
                      <CodeArea value={JSON.stringify(customerResponseObj || null, null, 2)} readOnly rows={12} />
                    ) : !customerResponseObj ? (
                      <div style={{ color: "#94a3b8" }}>No customer response submitted yet.</div>
                    ) : (
                      <div>
                        {CUSTOMER_RESPONSE_FIELDS.filter((f) => f.key !== "description" && f.key !== "message").map((f) => (
                          <DetailRow key={f.key} label={f.label} value={getCustomerResponseValue(customerResponseObj, f)} multiline={!!f.multiline} />
                        ))}

                        <div style={{ padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
                          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Description</div>

                          {customerHtmlLoading ? (
                            <div style={{ color: "#64748b", fontSize: 13 }}>Loading images…</div>
                          ) : customerHtml ? (
                            <div
                              style={{
                                fontSize: 13,
                                color: "#1e293b",
                                fontWeight: 500,
                                lineHeight: 1.5,
                                overflowWrap: "anywhere",
                              }}
                              dangerouslySetInnerHTML={{ __html: customerHtml }}
                            />
                          ) : (
                            <div style={{ color: "#94a3b8", fontSize: 13 }}>No description provided.</div>
                          )}
                        </div>
                      </div>
                    )}
                  </Card>

                  <Card
                    title="Public Customer Attachments"
                    right={
                      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#64748b", fontWeight: 700 }}>
                          <input type="checkbox" checked={custShowDeleted} onChange={(e) => setCustShowDeleted(e.target.checked)} disabled={busy} />
                          Show deleted
                        </label>
                        <SecondaryButton disabled={busy || custAttLoading} onClick={() => loadCustomerAttachments({ silent: false })}>
                          {custAttLoading ? "Loading..." : "Refresh"}
                        </SecondaryButton>
                      </div>
                    }
                  >
                    {custAttLoading ? (
                      <div style={{ color: "#64748b" }}>Loading attachments…</div>
                    ) : visibleCustomerAttachments.length === 0 ? (
                      <div style={{ color: "#94a3b8" }}>No attachments found.</div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {visibleCustomerAttachments.map((att) => {
                          const st = (att?.status || "").toUpperCase();
                          const isDeleted = st === "DELETED";
                          const uploadedTime = att.uploadedAt || att.confirmedAt || att.createdAt;
                          return (
                            <div
                              key={att.attachmentId}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 12,
                                padding: "12px 0",
                                borderBottom: "1px solid #f1f5f9",
                                opacity: isDeleted ? 0.6 : 1,
                              }}
                            >
                              <div style={{ minWidth: 260 }}>
                                <div style={{ fontWeight: 700, color: "#1e293b" }}>{att.filename || att.name || "(no filename)"}</div>
                                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                                  ID: <code>{att.attachmentId}</code>
                                </div>
                                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                                  {formatBytes(att.size)} · {prettyTime(uploadedTime)} · <b>{att.status || "-"}</b>
                                </div>
                              </div>

                              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <SecondaryButton disabled={busy || isDeleted} onClick={() => downloadCustomerAttachment(att)}>
                                  Download
                                </SecondaryButton>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                </div>
              ) : null}

              {/* Investigations */}
              {activeTab === "Investigations" ? (
                <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 24, alignItems: "start" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <Card
                      title="Log"
                      right={
                        <SecondaryButton disabled={busy} onClick={refreshInvestigations} title="Refresh investigations">
                          Refresh
                        </SecondaryButton>
                      }
                      noPadding
                    >
{(investigations || []).filter((x) => !x?.isDeleted).length === 0 ? (
  <div style={{ padding: 14, color: "#64748b" }}>No investigations.</div>
) : (
  (investigations || [])
    .filter((x) => !x?.isDeleted)
    .map((inv) => {

                          const active = selectedInvestigationId === inv.investigationId;
                          const actionPreview = String(inv?.investigationData?.actionTaken ||inv?.investigationData?.report?.incidentReportNo ||"").trim();
                          return (
                            <div
                              key={inv.investigationId}
                              onClick={() => selectInvestigation(inv.investigationId)}
                              style={{
                                padding: 12,
                                borderBottom: "1px solid #f1f5f9",
                                cursor: "pointer",
                                background: active ? "#f8fafc" : "transparent",
                              }}
                            >
<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
  <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>
    {inv.investigationNo || inv.investigationId}
  </div>

</div>


                              {actionPreview ? (
                                <div style={{ fontSize: 12, color: "#334155", marginTop: 6, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {actionPreview}
                                </div>
                              ) : (
                                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6, fontWeight: 600 }}>New investigation</div>
                              )}
                              <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{prettyTime(inv.updatedAt)}</div>
                            </div>
                          );
                        })
                      )}
                    </Card>

                    <Card title="Add Investigation">
                      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        <div style={{ fontSize: 13, color: "#64748b" }}>
                          Click once to create a new investigation entry. Fill in the report fields on the right panel.
                        </div>
                        <PrimaryButton disabled={busy} onClick={createInvestigation}>
                          Add Investigation
                        </PrimaryButton>
                      </div>
                    </Card>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <Card
                      title={
                        selectedInvestigationId
                          ? `Entry: ${
                              investigationDetails?.investigationNo ||
                              selectedInvestigation?.investigationNo ||
                              selectedInvestigationId
                            }`
                          : "Select an investigation"
                      }

                      right={
                        selectedInvestigationId ? (
                          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <SecondaryButton disabled={busy} onClick={refreshSelectedInvestigation}>
                              Reload
                            </SecondaryButton>
                            <SecondaryButton disabled={busy} onClick={listInvAttachments}>
                              Refresh Attachments
                            </SecondaryButton>
                          </div>
                        ) : null
                      }
                    >
                      {!selectedInvestigationId ? (
                        <div style={{ color: "#94a3b8" }}>Choose an investigation on the left to edit and manage attachments.</div>
                      ) : (
                        <>
                          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
                            Created: {prettyTime(investigationDetails?.createdAt || selectedInvestigation?.createdAt)} · Updated:{" "}
                            {prettyTime(investigationDetails?.updatedAt || selectedInvestigation?.updatedAt)}
                          </div>
                          {(() => {
  const m = investigationDetails?.investigationData?.meta || {};
  const who = m.lastEditedBy || m.lastEditedBySub;
  const when = m.lastEditedAt;
  if (!who && !when) return null;

  return (
    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
      Last edited by: <b>{who || "-"}</b>{when ? ` · ${prettyTime(when)}` : ""}
    </div>
  );
})()}


                          {/* ✅ use isReiriInv to decide form vs json */}
                          {isReiriInv ? (
                            <>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                                <div style={{ fontWeight: 800, color: "#1e293b" }}>Reiri Investigation Report</div>
                                <Segmented
                                  value={invEditorMode}
                                  onChange={setInvEditorMode}
                                  options={[
                                    { value: "form", label: "Form" },
                                    { value: "json", label: "JSON" },
                                  ]}
                                  disabled={busy}
                                />
                              </div>

                              {invEditorMode === "json" ? (
                                <CodeArea value={editInvestigationJson} onChange={(e) => setEditInvestigationJson(e.target.value)} rows={18} disabled={busy} />
                              ) : (
                                <>
                                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                                    <TextField
                                      label="Country / Affiliate"
                                      value={deepGet(invDraftObj, "report.countryAffiliate", "")}
                                      onChange={(e) => setInvField("report.countryAffiliate", e.target.value)}
                                      disabled={busy}
                                      placeholder="e.g. DASI"
                                    />
                                    <TextField
                                      label="Customer"
                                      value={deepGet(invDraftObj, "report.customer", "")}
                                      onChange={(e) => setInvField("report.customer", e.target.value)}
                                      disabled={busy}
                                      placeholder="Customer name"
                                    />
                                    <TextField
                                      label="Incident Report No"
                                      value={incidentReportNo || deepGet(invDraftObj, "report.incidentReportNo", "")}
                                      onChange={() => {}}
                                      disabled={true} // ✅ always locked
                                      placeholder=""
                                    />

                                    <TextField
                                      label="Date"
                                      value={deepGet(invDraftObj, "report.date", "")}
                                      onChange={(e) => setInvField("report.date", e.target.value)}
                                      disabled={busy}
                                      placeholder="YYYY-MM-DD"
                                    />
                                    <TextField
                                      label="Report By"
                                      value={deepGet(invDraftObj, "report.reportBy", "")}
                                      onChange={(e) => setInvField("report.reportBy", e.target.value)}
                                      disabled={busy}
                                      placeholder="Name"
                                    />
                                    <TextField
                                      label="Controller Serial Number"
                                      value={deepGet(invDraftObj, "report.controllerSerialNumber", "")}
                                      onChange={(e) => setInvField("report.controllerSerialNumber", e.target.value)}
                                      disabled={busy}
                                      placeholder="Serial number"
                                    />
                                    <TextField
                                      label="Software Version"
                                      value={deepGet(invDraftObj, "report.softwareVersion", "")}
                                      onChange={(e) => setInvField("report.softwareVersion", e.target.value)}
                                      disabled={busy}
                                      placeholder="e.g. v1.2.3"
                                    />
                                  </div>

                                  <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #f1f5f9" }}>
                                    <div style={{ fontWeight: 800, color: "#1e293b", marginBottom: 10 }}>Hardware Review</div>

                                    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
                                      <div style={{ display: "grid", gridTemplateColumns: "1fr 200px", gap: 12, alignItems: "start" }}>
                                        <div style={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>
                                          The product and boxed accessories are properly labeled and packed.
                                        </div>
                                        <SelectField
                                          label=""
                                          value={deepGet(invDraftObj, "hardwareReview.q1_packaging", "")}
                                          onChange={(e) => setInvField("hardwareReview.q1_packaging", e.target.value)}
                                          options={YES_NO_OPTIONS}
                                          disabled={busy}
                                          placeholder="Select..."
                                        />
                                      </div>

                                      <div style={{ display: "grid", gridTemplateColumns: "1fr 200px", gap: 12, alignItems: "start" }}>
                                        <div style={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>
                                          The hardware is free from all mechanical defects, impact damage, and signs of improper handling. 
                                        </div>
                                        <SelectField
                                          label=""
                                          value={deepGet(invDraftObj, "hardwareReview.q2_noDamage", "")}
                                          onChange={(e) => setInvField("hardwareReview.q2_noDamage", e.target.value)}
                                          options={YES_NO_OPTIONS}
                                          disabled={busy}
                                          placeholder="Select..."
                                        />
                                      </div>

                                      <div style={{ display: "grid", gridTemplateColumns: "1fr 200px", gap: 12, alignItems: "start" }}>
                                        <div style={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>
                                          The hardware is free from any post-factory modifications or alterations
                                        </div>
                                        <SelectField
                                          label=""
                                          value={deepGet(invDraftObj, "hardwareReview.q3_noModification", "")}
                                          onChange={(e) => setInvField("hardwareReview.q3_noModification", e.target.value)}
                                          options={YES_NO_OPTIONS}
                                          disabled={busy}
                                          placeholder="Select..."
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #f1f5f9" }}>
                                    <div style={{ fontWeight: 800, color: "#1e293b", marginBottom: 10 }}>Functionality Test</div>

                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                                      <SelectField
                                        label="Power Connection"
                                        value={deepGet(invDraftObj, "functionalityTest.powerConnection", "")}
                                        onChange={(e) => setInvField("functionalityTest.powerConnection", e.target.value)}
                                        options={FUNC_TEST_OPTIONS}
                                        disabled={busy}
                                        placeholder="Select..."
                                      />
                                      <SelectField
                                        label="Network Connection"
                                        value={deepGet(invDraftObj, "functionalityTest.networkConnection", "")}
                                        onChange={(e) => setInvField("functionalityTest.networkConnection", e.target.value)}
                                        options={FUNC_TEST_OPTIONS}
                                        disabled={busy}
                                        placeholder="Select..."
                                      />
                                      <SelectField
                                        label="RS485 Connection"
                                        value={deepGet(invDraftObj, "functionalityTest.rs485Connection", "")}
                                        onChange={(e) => setInvField("functionalityTest.rs485Connection", e.target.value)}
                                        options={FUNC_TEST_OPTIONS}
                                        disabled={busy}
                                        placeholder="Select..."
                                      />
                                      <SelectField
                                        label="USB Port Connection"
                                        value={deepGet(invDraftObj, "functionalityTest.usbPortConnection", "")}
                                        onChange={(e) => setInvField("functionalityTest.usbPortConnection", e.target.value)}
                                        options={FUNC_TEST_OPTIONS}
                                        disabled={busy}
                                        placeholder="Select..."
                                      />
                                      <SelectField
                                        label="DIO Connection"
                                        value={deepGet(invDraftObj, "functionalityTest.dioConnection", "")}
                                        onChange={(e) => setInvField("functionalityTest.dioConnection", e.target.value)}
                                        options={FUNC_TEST_OPTIONS}
                                        disabled={busy}
                                        placeholder="Select..."
                                      />
                                      <SelectField
                                        label="Touch Panel Response"
                                        value={deepGet(invDraftObj, "functionalityTest.touchPanelResponse", "")}
                                        onChange={(e) => setInvField("functionalityTest.touchPanelResponse", e.target.value)}
                                        options={FUNC_TEST_OPTIONS}
                                        disabled={busy}
                                        placeholder="Select..."
                                      />
                                    </div>
                                  </div>

                                  <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 14 }}>
                                    <TextAreaField
                                      label="Remarks"
                                      value={deepGet(invDraftObj, "remarks", "")}
                                      onChange={(e) => setInvField("remarks", e.target.value)}
                                      placeholder="Remarks..."
                                      disabled={busy}
                                      rows={3}
                                    />
                                    <TextAreaField
                                      label="Incident Description"
                                      value={deepGet(invDraftObj, "incidentDescription", "")}
                                      onChange={(e) => setInvField("incidentDescription", e.target.value)}
                                      placeholder="Describe the incident..."
                                      disabled={busy}
                                      rows={5}
                                    />
                                    <TextAreaField
                                      label="Evaluation and Analysis"
                                      value={deepGet(invDraftObj, "evaluationAndAnalysis", "")}
                                      onChange={(e) => setInvField("evaluationAndAnalysis", e.target.value)}
                                      placeholder="Evaluation and analysis..."
                                      disabled={busy}
                                      rows={6}
                                    />
                                  </div>

                                  <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #f1f5f9" }}>
                                    <div style={{ fontWeight: 800, color: "#1e293b", marginBottom: 10 }}>Prepared By</div>

<div style={{ gridColumn: "1 / -1" }}>
  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#334155" }}>
    <input
      type="checkbox"
      checked={useMySignature}
      disabled={busy}
      onChange={async (e) => {
  const checked = e.target.checked;
  setUseMySignature(checked);

  if (checked) {
    // ensure profile is loaded before save
    if (!profile) await loadProfile({ silent: true });
  }
}}

    />
    Use my signature
  </label>

  <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
    If unchecked, saving will remove any existing signature (prevents reusing someone else’s sign-off).
  </div>

  {useMySignature ? (
    <div style={{ marginTop: 10, padding: 12, borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
      {hasProfileSignature ? (
        <>
          <div style={{ fontSize: 12, color: "#334155", fontWeight: 800 }}>Using profile signature</div>
          <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
            File: <b>{profileSignature.filename || "signature"}</b>
            {profileSignature.updatedAt ? ` · Updated ${prettyTime(profileSignature.updatedAt)}` : ""}
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, color: "#991b1b", fontWeight: 800 }}>No profile signature uploaded</div>
          <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
            Go to <b>Profile</b> (top right) and upload your signature.
          </div>
        </>
      )}
    </div>
  ) : null}


  {deepGet(invDraftObj, "report.signatureOwner", "") ? (
    <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
      Current signature: <b>{deepGet(invDraftObj, "report.signatureOwner", "")}</b>
      {deepGet(invDraftObj, "report.signatureSignedAt", 0)
        ? ` · ${prettyTime(deepGet(invDraftObj, "report.signatureSignedAt", 0))}`
        : ""}
    </div>
  ) : (
    <div style={{ marginTop: 10, fontSize: 12, color: "#94a3b8" }}>
      Current signature: (none)
    </div>
  )}
</div>

                                  </div>

                                </>
                              )}

                              <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <PrimaryButton disabled={busy} onClick={patchInvestigation}>
                                  Save Investigation
                                </PrimaryButton>
                                <SecondaryButton
                                  disabled={busy}
                                  onClick={() => {
                                    const raw2 = investigationDetails?.investigationData || {};
                                    const normalized2 = normalizeReiriInvestigation(raw2, {
                                      affiliate: String(getReadableValue(incidentDataObj, "affiliate") || "").trim(),
                                      customer: String(incidentDataObj?.customer || incidentDataObj?.endCustomer || "").trim(),
                                      incidentId: selectedIncidentId,
                                    });
                                    setInvDraftObj(normalized2);
                                    setEditInvestigationJson(JSON.stringify(normalized2 || {}, null, 2));
                                    flashToast("Editor reset");
                                  }}
                                >
                                  Reset
                                </SecondaryButton>
                              </div>
                            </>
                          ) : (
                            <>
                              <CodeArea value={editInvestigationJson} onChange={(e) => setEditInvestigationJson(e.target.value)} rows={14} disabled={busy} />
                              <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <PrimaryButton disabled={busy} onClick={patchInvestigation}>
                                  Update Data
                                </PrimaryButton>
                                <SecondaryButton disabled={busy} onClick={() => setEditInvestigationJson(JSON.stringify(investigationDetails?.investigationData || {}, null, 2))}>
                                  Reset Editor
                                </SecondaryButton>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </Card>

                    {selectedInvestigationId ? (
                      <Card title="Attachments">
                        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                          <input type="file" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} disabled={busy} />
                          <PrimaryButton disabled={busy || !uploadFile} onClick={uploadInvAttachment}>
                            Upload
                          </PrimaryButton>
                          {uploadProgress ? <span style={{ fontSize: 12, color: "#64748b" }}>{uploadProgress}</span> : null}
                        </div>

                        {invAttachments.length === 0 ? (
                          <div style={{ color: "#94a3b8" }}>No attachments.</div>
                        ) : (
                          invAttachments.map((att) => {
                            const isDeleted = (att?.status || "").toUpperCase() === "DELETED";
                            return (
                              <div
                                key={att.attachmentId}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: 12,
                                  padding: "12px 0",
                                  borderBottom: "1px solid #f1f5f9",
                                  opacity: isDeleted ? 0.6 : 1,
                                }}
                              >
                                <div style={{ minWidth: 260 }}>
                                  <div style={{ fontWeight: 700, color: "#1e293b" }}>{att.filename}</div>
                                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                                    ID: <code>{att.attachmentId}</code>
                                  </div>
                                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                                    Status: <b>{att.status || "-"}</b> · Uploaded: {prettyTime(att.uploadedAt)}
                                  </div>
                                </div>

                                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                  <SecondaryButton disabled={busy || isDeleted} onClick={() => downloadInvAttachment(att)}>
                                    Download
                                  </SecondaryButton>
                                  <SecondaryButton disabled={busy || isDeleted} onClick={() => deleteInvAttachment(att)}>
                                    Delete
                                  </SecondaryButton>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </Card>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {activeTab === "Service Request" ? (
  <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
    <Card title="Service Request Form Link">
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>Days</label>
        <input
          type="number"
          min={1}
          max={30}
          value={srDays}
          disabled={busy}
          onChange={(e) => setSrDays(Math.max(1, Math.min(30, parseInt(e.target.value || "3", 10))))}
          style={{ width: 72, padding: 8, borderRadius: 8, border: "1px solid #e2e8f0" }}
        />

        <PrimaryButton disabled={busy} onClick={generateOrReopenServiceRequestLink}>
          Generate Link
        </PrimaryButton>

        <SecondaryButton
          disabled={busy || !serviceRequestUrl || isSrDisabled || isSrExpired}
          onClick={() => window.open(serviceRequestUrl, "_blank", "noopener,noreferrer")}
          title={isSrDisabled ? "Link disabled" : isSrExpired ? "Link expired (regenerate)" : "Open service request form"}
        >
          Open
        </SecondaryButton>

        {serviceRequestLink ? (
          <Pill status={isSrDisabled ? "DELETED" : isSrExpired ? "ARCHIVED" : "OPEN_TO_CUSTOMER"}>
            {isSrDisabled ? "Disabled" : isSrExpired ? "Expired" : "Active"}
          </Pill>
        ) : null}
      </div>

      {serviceRequestLink ? (
        <div style={{ marginTop: 16, fontSize: 12, background: "#f8fafc", padding: 12, borderRadius: 8 }}>
          <div style={{ marginBottom: 8 }}>
            <strong>Expires:</strong> {prettyTime(serviceRequestLink.expiresAt)}{" "}
            {serviceRequestLink.validDays ? `(${serviceRequestLink.validDays} days)` : ""}
          </div>

          {serviceRequestUrl ? (
            <div>
              <strong>URL:</strong> <code style={{ wordBreak: "break-all" }}>{serviceRequestUrl}</code>
            </div>
          ) : (
            <div style={{ color: "#64748b" }}>
              <strong>URL:</strong> (Not Available)
            </div>
          )}

          <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <SecondaryButton
              disabled={busy || !serviceRequestUrl}
              onClick={() => {
                navigator.clipboard.writeText(serviceRequestUrl);
                flashToast("URL copied");
              }}
            >
              Copy URL
            </SecondaryButton>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 14, color: "#64748b", fontSize: 13 }}>
          Generate link to open the Service Request Form (public link).
        </div>
      )}
    </Card>
{/* Del 
{serviceRequestSubmittedAt ? (
<Card title="Latest Service Request Submission">
  {renderReadableObject(serviceRequestDataObj)}
</Card>

) : (
  <Card title="Submission Status">
    <div style={{ color: "#64748b" }}>
      No Service Request submission recorded yet.
    </div>
  </Card>
)}*/}

{serviceRequestSubmittedAt ? (
  <Card title="Latest Service Request Submission">
    {(() => {
      var sr = serviceRequestDataObj || {};
      var sending = sr.sending || {};
      var receiving = sr.receiving || {};
      var logistics = (receiving && receiving.logistics) || {};

      function Row({ label, value }) {
        var v = value === undefined || value === null || String(value).trim() === "" ? "-" : String(value);
        return (
          <div style={{ padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>
              {label}
            </div>
            <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 600, whiteSpace: "pre-wrap" }}>
              {v}
            </div>
          </div>
        );
      }

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Sending */}
          <div>
            <div style={{ fontWeight: 900, color: "#0f172a", marginBottom: 8 }}>Sending</div>

            <Row label="Sender Name" value={sending.senderName} />
            <Row label="Sender Email" value={sending.senderEmail} />

            {/* ✅ Put these directly below Sender Email */}
            <Row label="Package Dimensions" value={sending.packageDimensions} />
            <Row label="Package Weight Kg" value={sending.packageWeightKg} />

            <Row label="Pickup Address" value={sending.pickupAddress} />
          </div>

          {/* Receiving */}
          <div>
            <div style={{ fontWeight: 900, color: "#0f172a", marginBottom: 8 }}>Receiving</div>

            <Row label="Shipment Option" value={receiving.shipmentOption} />
            <Row label="Receiver Name" value={receiving.receiverName} />
            <Row label="Receiver Email" value={receiving.receiverEmail} />
            <Row label="Delivery Address" value={receiving.deliveryAddress} />

            <div style={{ fontWeight: 900, color: "#0f172a", marginTop: 14, marginBottom: 8 }}>Logistics</div>
            <Row label="Company" value={logistics.company} />
            <Row label="Account Number" value={logistics.accountNumber} />
          </div>
        </div>
      );
    })()}
  </Card>
) : (
  <Card title="Submission Status">
    <div style={{ color: "#64748b" }}>
      No Service Request submission recorded yet.
    </div>
  </Card>
)}




  </div>
) : null}
{activeTab === "Warranty" ? (
  <WarrantyTab incidentDetails={incidentDetails} profile={profile} loadProfile={loadProfile} />
) : null}


            </div>
          </>
        ) : null}
      </div>

    </div> 

      {/* Busy Overlay */}
      {busy ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.6)",
            backdropFilter: "blur(2px)",
            display: "grid",
            placeItems: "center",
            zIndex: 9999,
          }}
        >
          <div style={{ padding: "16px 32px", background: "#1e293b", color: "white", borderRadius: 12, fontWeight: 700 }}>Working...</div>
        </div>
      ) : null}
    </div>
  );
}